import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './OrderDesign.css';
import './effects/RedeemDeathDissipation.css';
import { RedeemGhostMarkOverlay } from './effects/RedeemDeathDissipation';
import { AbilityIndicator } from './ability_display';
import { AbilitySymbolAnimation, useAbilityAnimations } from './ability_display/AbilitySymbolAnimation';
import LazyImage from './LazyImage';
import { getTargetingMediaBorderCss, getTargetingBorderClassName } from './targeting/TargetingBorder';
import { highlightAbilities } from './utils/highlightAbilities';
import { getOrderCardImageSrc as getOrderImageSrc } from './utils/orderCardImageSrc';
import { unitCannotInitiateAttack } from './utils/unitCannotInitiateAttack';
import {
  FRACTURE_MINDS_STRATEGEM_ID,
  getFractureMindsOutcomeForPlayer,
} from './utils/fractureMindsOutcome';
import { FaSkull } from 'react-icons/fa';
import {
  isInsideBattlefieldUnitExpandHotZoneWithUnion,
} from './utils/battlefieldUnitExpandHoverHotZone';
import {
  FACTION_SLAB_COLORS as factionColors,
  COMBINED_FACTION_STRATEGIES as combinedFactions,
} from './utils/factionUiColors';
import { CURSOR_Z_INDEX } from './config/cursorConfig';
import { ExploreUnknownAflameBurnOverlay } from './components/ExploreUnknownAflameBurnOverlay';
import { FriendlyAuraBorderEffect } from './components/FriendlyAuraBorderEffect';
import { NegativeAuraBorderEffect } from './components/NegativeAuraBorderEffect';
import { getCachedBorderImage, loadBorderImage } from './utils/borderImageCache';
import {
  isUnitConfused,
  shouldShowFriendlyAuraCloudEffect,
} from './utils/friendlyAuraProviderUnits';
import { shouldShowNegativeAuraCloudEffect } from './utils/negativeAuraProviderUnits';
import {
  LethalDeathParticleDissipation,
  LETHAL_DEATH_WHITE_WASH_RATIO,
  playLethalDeathDissipateSound,
} from './effects/LethalDeathParticleDissipation';
import {
  getOrderBadgeHealth,
  isOrderHealthDamagedDisplay,
} from './effects/battlefieldHealthDisplay';
// ✅ REMOVED: useUnitDamageAnimations, DamageAnimation - old damage animation system removed
// Two-phase attack system handles attack damage animations
// Slot death animations are handled separately by useSlotDeathDetection

const LETHAL_DEATH_FADE_DEFAULT_MS = 650;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const MINIMAL_DEPLOYED_CARD_FACE_SHELL = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  height: '140%',
  borderRadius: '8px',
  pointerEvents: 'none',
  zIndex: 0,
  overflow: 'hidden',
};

const OOR_DEPLOYED_CARD_FACE_SHELL = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -47%)',
  width: '78%',
  height: '138%',
  borderRadius: '8px',
  pointerEvents: 'none',
  zIndex: 0,
  overflow: 'hidden',
};

function withLethalDeathFadeClass(
  baseClass,
  isLethalDeathFading,
  isLethalDeathDissipating,
  isRedeemMarkedForDeath = false,
  isRedeemDeathFinishing = false
) {
  const classes = [(baseClass || '').trim()];
  if (isRedeemMarkedForDeath && !isRedeemDeathFinishing) {
    classes.push('order-design-redeem-marked-for-death');
  }
  if (isRedeemDeathFinishing) {
    classes.push('order-design-redeem-death-finishing');
  }
  if (isLethalDeathFading && !isLethalDeathDissipating && !isRedeemMarkedForDeath) {
    classes.push('order-design-lethal-death-fade');
  }
  if (isLethalDeathDissipating) {
    classes.push('order-design-lethal-death-dissipating');
  }
  return classes.filter(Boolean).join(' ');
}

function lethalDeathFadeStyle(isLethalDeathFading, lethalDeathFadeDurationMs, isLethalDeathDissipating) {
  if (!isLethalDeathFading || isLethalDeathDissipating) return undefined;
  const durationMs = prefersReducedMotion()
    ? lethalDeathFadeDurationMs
    : Math.round(lethalDeathFadeDurationMs * LETHAL_DEATH_WHITE_WASH_RATIO);
  return { '--order-lethal-fade-duration': `${durationMs}ms` };
}

function getOrderHealthOverlayClass(order, { ignoreDamaged = false } = {}) {
  const classes = [];
  if (order.bullyDamageFlash) classes.push('bully-damage-flash');
  if (order.healthCountdownPulse) classes.push('order-health-countdown-pulse');
  if (!ignoreDamaged && isOrderHealthDamagedColor(order)) classes.push('order-health-damaged');
  return classes.length > 0 ? classes.join(' ') : undefined;
}

function getOrderHealthShellClassName(order, { useHealthRatingShell = false, ignoreDamaged = false } = {}) {
  const overlay = getOrderHealthOverlayClass(order, { ignoreDamaged });
  if (useHealthRatingShell) {
    return ['order-health-rating', overlay].filter(Boolean).join(' ') || undefined;
  }
  return overlay || undefined;
}

function resolveOrderHealthTextColor(order, textColor) {
  if (textColor != null) {
    return textColor;
  }
  return isOrderHealthDamagedColor(order) ? '#ff4444' : 'white';
}

function OrderHealthBadgeShell({
  order,
  imageDraggable,
  style,
  useHealthRatingShell = false,
  healthIconClassName = '',
  textColor,
}) {
  if (order?.health == null) {
    return null;
  }

  const isAscensionGold = textColor != null;
  const shellClassName = [
    getOrderHealthShellClassName(order, { useHealthRatingShell, ignoreDamaged: isAscensionGold }),
    isAscensionGold ? 'order-health-ascension-gold' : null,
  ].filter(Boolean).join(' ') || undefined;

  return (
    <div
      className={shellClassName}
      style={getOrderHealthOverlayPulseStyle(order, style)}
    >
      <span
        className="order-health-countdown-anchor"
        data-health-fx-anchor={order.instanceId}
        style={{ color: resolveOrderHealthTextColor(order, textColor) }}
      >
        <OrderHealthIcon imageDraggable={imageDraggable} className={healthIconClassName} />
        <OrderHealthBadgeValue order={order} textColor={textColor} />
      </span>
    </div>
  );
}

function getOrderHealthOverlayPulseStyle(order, baseStyle = {}) {
  if (!order?.healthCountdownPulse) {
    return baseStyle;
  }
  return {
    ...baseStyle,
    overflow: 'visible',
    zIndex: 100,
  };
}

function OrderHealthBadgeValue({ order, textColor }) {
  return (
    <span
      className="order-health-countdown-anchor__value"
      data-health-fx-origin
      style={textColor != null ? { color: textColor } : undefined}
    >
      {getOrderBadgeHealth(order)}
    </span>
  );
}

function OrderHealthIcon({ imageDraggable, className = '' }) {
  return (
    <img
      draggable={imageDraggable}
      src="/images/health.png"
      alt="health"
      className={className}
      style={{
        width: '1.15em',
        height: '1.15em',
        verticalAlign: 'middle',
        marginRight: '2px',
        display: 'inline-block',
      }}
    />
  );
}

function isOrderHealthDamagedColor(order) {
  return isOrderHealthDamagedDisplay(order);
}

const MINIMAL_AFFORDABLE_GREEN_GLOW =
  '0 0 15px rgba(0, 255, 0, 0.9), 0 0 30px rgba(0, 255, 0, 0.7), 0 0 45px rgba(0, 255, 0, 0.5)';

/** Yellow = standard selection; orange = inspect-only (e.g. pinned expanded opponent unit). */
function getSelectionBorderCss(showTargetBorder, isSelected, inspectSelectionBorder) {
  if (showTargetBorder) {
    return getTargetingMediaBorderCss(true);
  }
  if (isSelected) {
    return inspectSelectionBorder ? '3px solid #ff8800' : '3px solid #ffff00';
  }
  return '0px solid #ccc';
}

/** Affordable green glow must live on a shell with overflow:visible — inner slab uses overflow:hidden and clips box-shadow on img/video. */
function getMinimalAffordableShellGlowStyle(showTargetBorder, isSelected, isPlayable) {
  if (showTargetBorder || isSelected || !isPlayable) return {};
  return { boxShadow: MINIMAL_AFFORDABLE_GREEN_GLOW };
}

/** Border on minimal card media (inside clipped slab). Protect orders overlay faction border art on top of the media — inner border is omitted when selected/targeted and drawn on top via ProtectSelectionHighlightRing. */
function getMinimalAffordableMediaBorder(showTargetBorder, isSelected, inspectSelectionBorder = false, isProtectOrder = false) {
  if (isProtectOrder && (showTargetBorder || isSelected)) {
    return { border: '0px solid #ccc' };
  }
  return { border: getSelectionBorderCss(showTargetBorder, isSelected, inspectSelectionBorder) };
}

/** Protect units must draw targeting above BorderImageWithTransparency (canvas z-index 5). */
const PROTECT_BORDER_ART_Z_INDEX = 5;
const PROTECT_TARGETING_HIGHLIGHT_Z_INDEX = PROTECT_BORDER_ART_Z_INDEX + 1;
function ProtectSelectionHighlightRing({
  showTargetBorder,
  isSelected,
  inspectSelectionBorder,
  isProtectOrder,
  inset = 0,
  borderRadius = '8px',
}) {
  if (!isProtectOrder || (!showTargetBorder && !isSelected)) return null;
  return (
    <div
      aria-hidden
      className={getTargetingBorderClassName(showTargetBorder)}
      style={{
        position: 'absolute',
        inset,
        borderRadius,
        border: getSelectionBorderCss(showTargetBorder, isSelected, inspectSelectionBorder),
        zIndex: PROTECT_TARGETING_HIGHLIGHT_Z_INDEX,
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }}
    />
  );
}

const ordersStyles = {
  container: {
    position: 'relative',
    border: '0px solid #ccc',
    borderRadius: '8px',
    padding: '20px 5px',
    margin: '10px',
    width: '250px',
    height: '380px',
    fontFamily: "'Exo 2', sans-serif",
    fontWeight: '400',
    color: 'white',
    overflow: 'visible',
    boxSizing: 'border-box',
    zIndex: 2,
    cursor: 'pointer',
    textAlign: 'left', // Explicitly set to prevent inheritance from parent
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '50%',
    objectFit: 'cover',
    zIndex: 0,
    pointerEvents: 'none',
  },
  contentWrapper: {
    position: 'relative',
    zIndex: 1,
  },
  top: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: '5px 10px',
    borderRadius: '4px',
    marginTop: '-15px',
    boxSizing: 'border-box',
  },
  middle: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: '5px 10px',
    borderRadius: '4px',
    marginTop: '120px',
  },
  lower: {
    position: 'absolute',
    top: '50%',
    left: 0,
    width: '100%',
    padding: '10px',
    paddingRight: '48px', // Reserve space for ability icons on the right (24px icon + 8px gap + 8px margin + 8px padding)
    fontSize: '0.95em',
    boxSizing: 'border-box',
    zIndex: 1,
    textAlign: 'left', // Explicitly set to prevent inheritance from parent
  },
  bottom: {
    position: 'absolute',
    bottom: '0px',
    width: '100%',
    textAlign: 'center',
    fontSize: '0.9em',
    zIndex: 1,
    padding: '5px 10px',
    boxSizing: 'border-box',
  },
};

const EXPANDED_CARD_UNIFORM_SCALE = 0.81;

function FractureMindsPoweredSkullOverlay({ show, iconSizePx = 22 }) {
  if (!show) return null;
  return (
    <div
      role="img"
      aria-label="Fracture Minds: destroy effect active"
      title="Fracture Minds: destroys the target (4+ Asrah units deployed)"
      style={{
        position: 'absolute',
        top: '4px',
        right: '4px',
        zIndex: 8,
        lineHeight: 0,
        pointerEvents: 'none',
      }}
    >
      <FaSkull
        className="order-design-fracture-minds-skull-pulse"
        style={{
          color: '#e85d5d',
          fontSize: `${iconSizePx}px`,
          display: 'block',
        }}
        aria-hidden
      />
    </div>
  );
}

// Build 3D slab gradient and shadow from a hex faction color (reduced thickness for slimmer mahjong-stone look)
function get3DSlabStyles(hexColor) {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const light = `rgb(${Math.min(r + 40, 255)}, ${Math.min(g + 40, 255)}, ${Math.min(b + 40, 255)})`;
  const mid = `rgb(${r}, ${g}, ${b})`;
  const dark = `rgb(${Math.max(r - 40, 0)}, ${Math.max(g - 40, 0)}, ${Math.max(b - 40, 0)})`;
  const edge1 = `rgb(${Math.max(r - 55, 0)}, ${Math.max(g - 55, 0)}, ${Math.max(b - 55, 0)})`;
  const edge2 = `rgb(${Math.max(r - 70, 0)}, ${Math.max(g - 70, 0)}, ${Math.max(b - 70, 0)})`;
  return {
    background: `linear-gradient(to bottom, ${light} 0%, ${mid} 50%, ${dark} 100%)`,
    boxShadow: `0 1px 0 ${edge1}, 0 2px 0 ${edge2}, 0 3px 6px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.15)`,
  };
}

// Incoming light from lower-left for minimized mahjong-style cards (overlay on the 3D slab)
const minimalSlabLightFromLowerLeft = {
  position: 'absolute',
  inset: 0,
  borderRadius: '8px',
  background: 'radial-gradient(ellipse 130% 130% at 0% 100%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 40%, transparent 65%)',
  pointerEvents: 'none',
  zIndex: 0,
};

// Helper function to get abilities from order
function getAbilities(order) {
  if (!order) return [];
  // ✅ FIX: Check instanceAbilities first if hasInstanceModifications is true
  // This ensures dynamically added abilities (e.g., Blitz from Exolot Finisher) are included
  if (order.hasInstanceModifications && Array.isArray(order.instanceAbilities)) {
    return order.instanceAbilities;
  }
  if (Array.isArray(order.abilities)) return order.abilities;
  if (typeof order.ability === "string") return [order.ability];
  if (Array.isArray(order.abilitiesList)) return order.abilitiesList;
  return [];
}

function isNonUnitOrderType(order) {
  const orderType = order?.type;
  return orderType === 'Strategem' || orderType === 'Stratagem' || orderType === 'Weapon' || orderType === 'Drone';
}

// Helper function to check if a unit has Blitz ability
// Matches backend logic in battleEngine.js::canOrderAttack()
// CRITICAL: Checks instanceAbilities first if hasInstanceModifications is true
// This ensures dynamically added abilities (e.g., Blitz from Exolot Finisher) are detected
function hasBlitzAbility(order) {
  if (!order) return false;
  
  // ✅ CRITICAL: Check instanceAbilities first if hasInstanceModifications is true
  // This ensures dynamically added abilities (e.g., Blitz from Exolot Finisher) are detected
  if (order.hasInstanceModifications && Array.isArray(order.instanceAbilities)) {
    return order.instanceAbilities.includes("Blitz");
  }
  
  // Fallback to regular abilities array
  if (Array.isArray(order.abilities)) {
    return order.abilities.includes("Blitz");
  }
  
  // Fallback to single ability string
  if (typeof order.ability === "string") {
    return order.ability === "Blitz";
  }
  
  return false;
}

// Engrave-style text for Protect unit title/cost (white with carved-in shadow)
const protectPlatingEngraveStyle = {
  color: '#ffffff',
  fontFamily: "'Cinzel', 'Rockwell', 'Engravers MT', 'Times New Roman', serif",
  fontWeight: '600',
  textShadow: '1px 1px 2px rgba(0,0,0,0.8), -1px -1px 1px rgba(255,255,255,0.15), 0 0 1px rgba(0,0,0,0.5)',
};
// Dark engraved text for plating + thin white shadow for readability
const protectPlatingEngraveStyleDark = {
  ...protectPlatingEngraveStyle,
  color: '#000',
  textShadow: '1px 1px 2px rgba(0,0,0,0.8), -1px -1px 1px rgba(255,255,255,0.15), 0 0 1px rgba(0,0,0,0.5), 0 0 2px rgba(255,255,255,0.5)',
};
// Helper function to get the correct cost using Ability Array System
function getDisplayCost(order) {
  if (!order) return 0;
  
  // Use instanceCost if hasInstanceModifications is true, otherwise use regular cost
  if (order.hasInstanceModifications && order.instanceCost !== undefined) {
    return order.instanceCost;
  }
  
  return order.cost || 0;
}

// Helper function to get border image path based on faction
function getBorderImagePath(order) {
  if (!order) return '/images/border_shadow.png';
  
  // Use order.faction for color lookup (always English for matching)
  const factionForColor = order.faction || order.faction_name;
  const hasProtectAbility = !isNonUnitOrderType(order) && (
    getAbilities(order).includes("Protect") || order.displayAsProtectUnit === true
  );
  
  // Check if order belongs to multiple factions (use border_mix.png)
  // These orders previously had yellow backgrounds (#FFA500 - Combined color)
  // We use the same logic that determines yellow background color
  
  // Method 3: Fallback - Check hardcoded list of known multi-faction orders
  // These are orders that belong to multiple factions but may not have a combined faction name
  // This list can be updated as more multi-faction orders are identified
  const multiFactionOrderIds = [
    393, // Essia Luhr
    341, // The Betrayal
    // Add more order IDs that belong to multiple factions here as they are identified
  ];

  const isMultiFactionOrder = (
    order.belongs_to_multiple_factions === true ||
    order.is_multi_faction === true ||
    Boolean(combinedFactions[factionForColor]) ||
    Boolean(order.id && multiFactionOrderIds.includes(order.id))
  );

  if (isMultiFactionOrder) {
    return hasProtectAbility ? '/images/border_protect_multifaction.png' : '/images/border_mix.png';
  }

  // Faction-specific Protect borders
  if (hasProtectAbility) {
    const protectBorderMap = {
      'Remnants of Humanity': '/images/border_protect_remnants.png',
      'League of Shadows': '/images/border_protect_league.png',
      "Ra's Children": '/images/border_protect_ra.png',
      'Exolot Dominion': '/images/border_protect_exolot.png',
      'The Sentients': '/images/border_protect_sentients.png',
      'Asrah': '/images/border_protect_asrah.png',
    };
    if (protectBorderMap[factionForColor]) {
      return protectBorderMap[factionForColor];
    }
  }
  
  // Map faction names to border files (single-faction orders only)
  const borderMap = {
    'Remnants of Humanity': '/images/border_humans.png',
    'Exolot Dominion': '/images/border_exolot.png',
    "Ra's Children": '/images/border_ra.png',
    'Asrah': '/images/border_asrah.png',
    'The Sentients': '/images/border_sentients.png',
    'League of Shadows': '/images/border_shadow.png',
  };
  
  return borderMap[factionForColor] || '/images/border_shadow.png';
}

// Helper function to check if a unit has a premium video
// Premium videos are located in /premium/ and follow the pattern {unitId}p.mp4
// Checks the order object for hasPremium property (set by backend from database)
function hasPremiumVideo(order) {
  if (!order) return false;
  
  // ✅ PRIORITY 1: Check instance premium status (set during deployment)
  // This is the most important check - if a player deployed a unit with premium art,
  // everyone (opponent, spectators) should see it as premium
  if (order.hasPremium === true) {
    if (order.id === 74 || order.id === 350) {
      console.log(`✨ OrderDesign: Premium detected for order ${order.id} via instance hasPremium property`);
    }
    return true;
  }
  
  // ✅ PRIORITY 2: Check order premium status from database (for hand/deck cards)
  // This is for cards in hand/deck that the current user owns
  if (order.has_premium === true) {
    if (order.id === 74 || order.id === 350) {
      console.log(`✨ OrderDesign: Premium detected for order ${order.id} via order has_premium property`);
    }
    return true;
  }
  
  // No fallback - premium should only be shown if:
  // 1. The unit was deployed with premium (hasPremium === true) - everyone sees it
  // 2. The current user owns premium (has_premium === true) - only in their strategy/hand
  return false;
}

// Helper function to get premium video path for a unit
function getPremiumVideoPath(order) {
  if (!hasPremiumVideo(order)) return null;
  const unitId = order.id || order.order_id;
  if (!unitId) return null;
  const id = typeof unitId === 'string' ? parseInt(unitId, 10) : unitId;
  return `/premium/${id}p.mp4`;
}

// Helper function to get image object-position adjustments based on unit ID
// Returns an object with objectPosition to show different parts of the image
// This changes which section of the image is visible (not the image element position)
function getImagePositionAdjustment(unitId) {
  if (!unitId) return { objectPosition: undefined };
  
  // Convert to number if it's a string to handle both cases
  const id = typeof unitId === 'string' ? parseInt(unitId, 10) : unitId;
  
  // Map of unit IDs to their object-position values
  // object-position controls which part of the image is visible when using object-fit: cover
  // Examples:
  // - 'center top' - shows top center portion
  // - 'center center' - shows center (default)
  // - 'center 60%' - shows lower portion (60% down from top)
  // - 'center bottom' - shows bottom portion
  // - '50% 60%' - shows 50% horizontal, 60% vertical
  const imageAdjustments = {
    // Unit 211: Show higher portion of image (moved up one cell from 50% to 40%)
    // Lower percentage = higher up in the image (shows more of the top)
    211: { objectPosition: 'center 40%' },
    '211': { objectPosition: 'center 40%' }, // Also handle string version
    // Example: Add unit IDs here with their desired object-position values
    // 123: { objectPosition: 'center 70%' }, // Show even lower portion
    // 456: { objectPosition: 'center top' }, // Show top portion
  };
  
  return imageAdjustments[id] || imageAdjustments[unitId] || { objectPosition: undefined };
}

// Component to render border image with white-to-transparent conversion for all factions
// Fixes colored box issue for Ra's Children, Asrah, League of Shadows, and combined factions
function BorderImageWithTransparency({ src, faction, onLoad, styleOverrides }) {
  const canvasRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);

  useEffect(() => {
    if (!canvasRef.current || !src) return;

    const canvas = canvasRef.current;
    let cancelled = false;

    const applyProcessed = (processed) => {
      if (cancelled || !canvasRef.current) return;
      canvas.width = processed.imageData.width;
      canvas.height = processed.imageData.height;
      setAspectRatio(processed.aspectRatio);
      const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: false });
      if (!ctx) {
        throw new Error(`BorderImageWithTransparency: 2d context unavailable for ${src}`);
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.putImageData(processed.imageData, 0, 0);
      setImageLoaded(true);
      if (onLoad) {
        onLoad(processed.naturalWidth, processed.naturalHeight);
      }
    };

    const cached = getCachedBorderImage(src);
    if (cached) {
      applyProcessed(cached);
      return undefined;
    }

    loadBorderImage(src)
      .then(applyProcessed)
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setImageLoaded(false);
      });

    return () => {
      cancelled = true;
    };
  }, [src, faction, onLoad]);
  
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '250px', // Original size
        height: `${250 * aspectRatio}px`, // Maintain aspect ratio
        zIndex: 5,
        pointerEvents: 'none',
        transform: 'translate(-50%, -50%) scale(1.56)', // Scale 56% larger from center (1.3 * 1.2 = 1.56)
        transformOrigin: 'center center',
        display: imageLoaded ? 'block' : 'none',
        background: 'transparent', // Ensure canvas background is transparent
        ...styleOverrides,
      }}
    />
  );
}

function OrderDesign({ order, count, minimal = false, onClick, isOOR = false, isSelected = false, inspectSelectionBorder = false, isTarget = false, forceTargetAppearance = false, targetMode = null, debugInfo = null, lazyLoad = false, zoneName = null, slotIndex = null, gameState = null, userId = null, isPlayable = false, noMargin = false, showOwnedCounter = false, ownedCount = 0, maxCount = 3, rarity = null, isHiddenDuringAnimation = false, isLethalDeathFading = false, isRedeemMarkedForDeath = false, isRedeemDeathFinishing = false, showCaropaeraRedeemGhost = false, lethalDeathFadeDurationMs = LETHAL_DEATH_FADE_DEFAULT_MS, onLethalDeathFadeComplete = null, isMulligan = false, isCounterStrike = false, renderButtons = null, scale = 1, imageDraggable = true, isViewedByOpponent = false, onBattlefieldExpandedPointerLeave = null, passThroughExpandedPortalForTargeting = false, fixedDesktopExpandedPosition = null, mediaOnly = false, hideExpandedPortalSourceSlot = false, ascensionHighlights = null }) {
  const showTargetBorder = isTarget || forceTargetAppearance;
  const [imageHovered, setImageHovered] = useState(false);
  const containerRef = useRef(null);
  const minimalContainerRef = useRef(null);
  const expandedAbilityIconsRowRef = useRef(null);
  const expandedActionButtonsRef = useRef(null);
  const onBattlefieldExpandedPointerLeaveRef = useRef(onBattlefieldExpandedPointerLeave);
  onBattlefieldExpandedPointerLeaveRef.current = onBattlefieldExpandedPointerLeave;
  const [expandedPosition, setExpandedPosition] = useState({ top: 0, left: 0 });
  const [isTouching, setIsTouching] = useState(false);
  const lethalCardFaceRef = useRef(null);
  const [lethalDissipation, setLethalDissipation] = useState(null);
  // Keep latest completion callback without re-triggering the death sequence on every parent re-render.
  const onLethalDeathFadeCompleteRef = useRef(onLethalDeathFadeComplete);
  onLethalDeathFadeCompleteRef.current = onLethalDeathFadeComplete;
  // One-shot guard so the dissipation can only fire a single time per death.
  const lethalDeathStartedRef = useRef(false);

  useEffect(() => {
    if (isRedeemMarkedForDeath) {
      lethalDeathStartedRef.current = false;
      setLethalDissipation(null);
      return undefined;
    }
    if (!isLethalDeathFading) {
      lethalDeathStartedRef.current = false;
      setLethalDissipation(null);
      return undefined;
    }

    if (lethalDeathStartedRef.current) {
      return undefined;
    }
    if (typeof onLethalDeathFadeCompleteRef.current !== 'function') {
      return undefined;
    }
    lethalDeathStartedRef.current = true;
    playLethalDeathDissipateSound();

    if (prefersReducedMotion()) {
      const timer = window.setTimeout(() => {
        onLethalDeathFadeCompleteRef.current?.();
      }, lethalDeathFadeDurationMs);
      return () => window.clearTimeout(timer);
    }

    const whiteWashMs = Math.round(lethalDeathFadeDurationMs * LETHAL_DEATH_WHITE_WASH_RATIO);
    const particleMs = lethalDeathFadeDurationMs - whiteWashMs;

    const whiteWashTimer = window.setTimeout(() => {
      const rect = lethalCardFaceRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) {
        onLethalDeathFadeCompleteRef.current?.();
        return;
      }
      setLethalDissipation({
        anchorRect: {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        },
        durationMs: particleMs,
      });
    }, whiteWashMs);

    return () => {
      window.clearTimeout(whiteWashTimer);
    };
  }, [isLethalDeathFading, isRedeemMarkedForDeath, lethalDeathFadeDurationMs]);

  const handleLethalParticleComplete = () => {
    setLethalDissipation(null);
    onLethalDeathFadeCompleteRef.current?.();
  };

  const lethalDeathParticlePortal = lethalDissipation ? (
    <LethalDeathParticleDissipation
      anchorRect={lethalDissipation.anchorRect}
      durationMs={lethalDissipation.durationMs}
      onComplete={handleLethalParticleComplete}
    />
  ) : null;

  // Unique ID for this order instance (for global expand tracking)
  const orderInstanceId = useRef(`order_${order?.instanceId || order?.id}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Detect mobile landscape mode
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerHeight <= 500 || (window.innerWidth <= 500 && window.innerHeight <= 932);
      return hasTouch && isSmallScreen;
    }
    return false;
  });
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const checkMobile = () => {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerHeight <= 500 || (window.innerWidth <= 500 && window.innerHeight <= 932);
      setIsMobile(hasTouch && isSmallScreen);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);
  
  // Listen for global "close all expanded" events (ensures only one card expanded at a time)
  useEffect(() => {
    if (!isMobile) return;
    
    const handleCloseAll = (e) => {
      // If exceptId is null, close ALL cards
      // Otherwise, close this card if it's not the one that just opened
      if (e.detail?.exceptId === null || (e.detail?.exceptId !== orderInstanceId.current && isTouching)) {
        setIsTouching(false);
      }
    };
    
    window.addEventListener('orderdesign-close-all', handleCloseAll);
    return () => window.removeEventListener('orderdesign-close-all', handleCloseAll);
  }, [isMobile, isTouching]);
  
  // Window-level touch end listener to handle cases where DOM element changes mid-touch
  // (e.g., OOR falls through to regular view)
  useEffect(() => {
    if (!isMobile || !isTouching) return;
    
    const handleGlobalTouchEnd = (e) => {
      // Small delay to ensure this runs after other handlers
      setTimeout(() => {
        setIsTouching(false);
      }, 10);
    };
    
    // Use capture phase to ensure we catch the event
    window.addEventListener('touchend', handleGlobalTouchEnd, { capture: true });
    window.addEventListener('touchcancel', handleGlobalTouchEnd, { capture: true });
    
    return () => {
      window.removeEventListener('touchend', handleGlobalTouchEnd, { capture: true });
      window.removeEventListener('touchcancel', handleGlobalTouchEnd, { capture: true });
    };
  }, [isMobile, isTouching]);
  
  // Mobile scale for expanded cards (smaller than desktop)
  const mobileExpandScale = 0.55;
  
  // Handle touch events for mobile expanded view - works for ALL orders on mobile
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    
    // Check if there are actual touch points
    if (e.touches && e.touches.length > 0) {
      const touch = e.touches[0];
      if (!touch) return;
      
      const touchX = touch.clientX;
      const touchY = touch.clientY;
      
      // Expanded card dimensions (56% larger than base - 250px * 1.56 = 390px)
      const expandedCardWidth = 390; // Actual expanded card width (250 * 1.56)
      const baseCardHeight = 380;
      const buttonSpace = 40; // Reduced for mobile
      const baseTotalHeight = baseCardHeight + buttonSpace;
      
      // Mobile uses smaller scale
      const effectiveScale = mobileExpandScale;
      
      // SCALED dimensions - use expanded card width for accurate clamping
      const scaledCardWidth = expandedCardWidth * effectiveScale;
      const scaledTotalHeight = baseTotalHeight * effectiveScale;
      
      const padding = 10;
      const offsetFromFinger = 20; // Distance from finger to card edge
      
      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // ALWAYS try to place card to the right of finger first
      let left = touchX + offsetFromFinger;
      let top = touchY - (scaledTotalHeight / 4); // Offset up a bit so card is visible
      
      // Check if card fits on the right (using SCALED width)
      if (left + scaledCardWidth > viewportWidth - padding) {
        // Not enough space on right, place on left side
        left = touchX - scaledCardWidth - offsetFromFinger;
      }
      
      // Ensure left doesn't go off screen
      if (left < padding) {
        left = padding;
      }
      
      // Adjust vertical position to keep card in viewport (using SCALED height)
      if (top < padding) {
        top = padding;
      } else if (top + scaledTotalHeight > viewportHeight - padding) {
        top = viewportHeight - padding - scaledTotalHeight;
        if (top < padding) {
          top = padding;
        }
      }
      
      // Close all other expanded cards first
      window.dispatchEvent(new CustomEvent('orderdesign-close-all', { 
        detail: { exceptId: orderInstanceId.current } 
      }));
      
      setExpandedPosition({ top, left });
      setIsTouching(true);
    }
  };
  
  const handleTouchEnd = (e) => {
    if (!isMobile) return;
    setIsTouching(false);
    // Dispatch close-all event to ensure no other cards remain open
    window.dispatchEvent(new CustomEvent('orderdesign-close-all', { 
      detail: { exceptId: null } // Close ALL cards, including this one
    }));
  };
  
  const handleTouchCancel = (e) => {
    if (!isMobile) return;
    setIsTouching(false);
    // Dispatch close-all event to ensure no other cards remain open
    window.dispatchEvent(new CustomEvent('orderdesign-close-all', { 
      detail: { exceptId: null }
    }));
  };
  
  // Use the isOOR prop (passed from BattleUI as order.isOOR)
  // Also check order.isOOR directly as fallback in case prop isn't set
  const actualIsOOR = isOOR || order?.isOOR || false;
  
  // Extract playerId from gameState and zoneName for repositioning detection
  let playerId = null;
  if (gameState && zoneName) {
    // zoneName format: "your_front", "your_flank", "opponent_front", "opponent_flank"
    const allPlayerIds = gameState.players || Object.keys(gameState.state || {});
    if (zoneName.startsWith('your_')) {
      // This is the current user's unit - use first playerId (usually the current user)
      // In BattleUI, "your_" refers to userId, which is typically the first player
      playerId = allPlayerIds[0] || null;
    } else if (zoneName.startsWith('opponent_')) {
      // This is the opponent's unit - use second playerId (or the one that's not userId)
      playerId = allPlayerIds.length > 1 ? allPlayerIds[1] : (allPlayerIds[0] || null);
    }
  }
  
  // Get current player's intelligence for cost circle glow effect
  // Only calculate when order is in hand (not in a zone) and gameState is available
  const getPlayerIntelligence = () => {
    if (!gameState || !gameState.state || zoneName) return null; // Only for hand orders
    
    // Use the userId prop directly if provided (most reliable)
    if (userId && gameState.state[userId]) {
      const intelligence = gameState.state[userId].intelligence;
      return intelligence !== undefined && intelligence !== null ? intelligence : null;
    }
    
    // Fallback: Try multiple methods to get the current player ID
    let currentPlayerId = null;
    
    // Method 1: Use gameState.players array (first player is typically current user)
    if (gameState.players && Array.isArray(gameState.players) && gameState.players.length > 0) {
      currentPlayerId = gameState.players[0];
    }
    
    // Method 2: If players array doesn't work, try getting first key from state
    if (!currentPlayerId && gameState.state) {
      const stateKeys = Object.keys(gameState.state);
      if (stateKeys.length > 0) {
        currentPlayerId = stateKeys[0];
      }
    }
    
    // Method 3: Check if there's a 'turn' property that indicates current player
    if (!currentPlayerId && gameState.turn) {
      currentPlayerId = gameState.turn;
    }
    
    if (!currentPlayerId || !gameState.state[currentPlayerId]) return null;
    
    const intelligence = gameState.state[currentPlayerId].intelligence;
    // Return the actual intelligence value (0 is valid, so we check for undefined/null)
    return intelligence !== undefined && intelligence !== null ? intelligence : null;
  };
  
  const playerIntelligence = getPlayerIntelligence();
  
  // ✅ REMOVED: Old damage animation system - two-phase attack system handles attack damage animations
  // Slot death animations are handled separately by useSlotDeathDetection
  const damageAnimations = [];
  const hasAnimationsPlaying = false;
  
  // Track ability animations for minimal view
  const { animatingAbilities, removeAnimation } = useAbilityAnimations(order, minimal && !mediaOnly, zoneName);
  
  // Check if unit is confused (both server flags — same gate as aura/description stripping)
  const isConfused = isUnitConfused(order);
  // Normalize immobilize flag from backend (strict checks only)
  const isImmobilized = (order.isImmobilized === true) || (order.immobilized === true) || (order.status === 'immobilized');
  const immobilizedImageFilter = isImmobilized ? 'brightness(2.6) contrast(1.35) saturate(0.35)' : 'none';
  const isExploreUnknownAflame = order.isExploreUnknownAflame === true;
  const aflameImageFilter = isExploreUnknownAflame
    ? 'saturate(1.5) brightness(1.2) sepia(0.45) hue-rotate(-18deg)'
    : 'none';
  const boardStatusImageFilter = isImmobilized
    ? immobilizedImageFilter
    : isExploreUnknownAflame
      ? aflameImageFilter
      : 'none';
  
  // Prefer canonical English `faction` (server sets it from factions.name); `faction_name` matches for /orders list.
  const factionForColor = order.faction ?? order.faction_name;
  const color = isConfused 
    ? '#404040' // Dark gray for confused units
    : order.isMission5Corn === true
      ? '#0A1F4D' // Mission 5 Corn token: dark blue slab
      : order.isExploreUnknownMemory === true
        ? '#1a0d2e' // Explore Unknown Memory token: deep violet slab
      : combinedFactions[factionForColor]
        ? factionColors['Combined']
        : factionColors[factionForColor] || '#FFFFFF';

  // Stone (3D slab) color for minimal view: silver for multi-faction orders (e.g. Lana Monares), faction color otherwise
  const stoneColor = (combinedFactions[factionForColor] && !isConfused) ? '#C0C0C0' : color;
  const showFriendlyAuraCloud = shouldShowFriendlyAuraCloudEffect(order, {
    zoneName,
    isHiddenDuringAnimation,
  });
  const showNegativeAuraCloud = shouldShowNegativeAuraCloudEffect(order, {
    zoneName,
    isHiddenDuringAnimation,
  });

  // Use prop maxCount if showOwnedCounter is set (from Strategy.js), otherwise calculate from is_unique
  const effectiveMaxCount = showOwnedCounter ? maxCount : (order.is_unique ? 1 : 3);
  const isDisabled = count >= effectiveMaxCount;

  const expandedPortalPassesThrough = Boolean(zoneName && passThroughExpandedPortalForTargeting);
  const expandedPortalPointerEvents = isDisabled
    ? 'none'
    : expandedPortalPassesThrough
      ? 'none'
      : 'auto';
  
  // Get image object-position adjustment for this unit (which part of image to show)
  const imageAdjustment = getImagePositionAdjustment(order.id);
  
  // Check for different abilities
  const abilities = getAbilities(order);
  // Always use normal title/cost styling (no Protect plating style)
  const showProtectPlating = false;
  const isProtectOrder = !isNonUnitOrderType(order) && (
    abilities.includes("Protect") || order.displayAsProtectUnit === true
  );
  const hasShieldAbility = abilities.includes("Shield");
  const hasActiveShield =
    hasShieldAbility &&
    (order.hasShield === true ||
      (!isConfused && order.hasShield == null && order.playedTurn !== undefined));
  // ✅ FIX: Use helper function that matches backend logic for checking Blitz
  // This ensures dynamically added Blitz (e.g., from auras) is properly detected
  const hasBlitz = hasBlitzAbility(order);
  
  // Determine if unit cannot attack (shared with BattleUI keyboard shortcuts)
  const attackReadinessOptions = useMemo(() => {
    if (!userId) return {};
    if (!gameState?.state) return { activePlayerId: userId };
    const allPlayerIds = gameState.players || Object.keys(gameState.state);
    const opponentPlayerId = allPlayerIds.find((id) => String(id) !== String(userId));
    return opponentPlayerId
      ? { activePlayerId: userId, opponentPlayerId }
      : { activePlayerId: userId };
  }, [userId, gameState]);
  const cannotAttack = unitCannotInitiateAttack(order, gameState, zoneName, attackReadinessOptions);

  const fractureMindsPoweredSkull = useMemo(() => {
    if (!order || Number(order.id) !== FRACTURE_MINDS_STRATEGEM_ID) return false;
    const ot = order.type;
    if (ot !== 'Strategem' && ot !== 'Stratagem') return false;
    if (!gameState?.state || userId == null || userId === '') return false;
    const p = gameState.state[userId];
    if (!p) return false;
    return getFractureMindsOutcomeForPlayer(p) === 'destroy';
  }, [order, gameState, userId]);

  // Calculate position for expanded view (portal) - MUST be before any conditional returns
  // 
  // Portal rendering conditions:
  // - Desktop: only when minimal=false (non-minimal cards like expanded hand cards)
  // - Mobile: only when isTouching=true AND minimal=true (handled by touch handlers, not this effect)
  //
  // So this effect only needs to run for desktop non-minimal cards.
  // For these cards, containerRef (not minimalContainerRef) is attached to the visible element.
  //
  // IMPORTANT: The expanded card is rendered via portal to document.body (OUTSIDE viewport-container)
  // So we use screen coordinates from getBoundingClientRect() directly.
  // The card is scaled via transform: scale(${scale}), so we need to account for scaled dimensions.
  useEffect(() => {
    // Calculate position for expanded view portal
    // Use minimalContainerRef - it's the wrapper that stays at the correct position in the DOM
    // (even when minimal=false, the wrapper is still there with opacity: 0)
    // containerRef is attached to the portal itself, so using it would be circular!
    // Mobile TOUCH position is handled separately by touch handlers
    if (!minimal && minimalContainerRef.current) {
      if (
        !isMobile &&
        fixedDesktopExpandedPosition &&
        Number.isFinite(fixedDesktopExpandedPosition.top) &&
        Number.isFinite(fixedDesktopExpandedPosition.left)
      ) {
        setExpandedPosition({
          top: fixedDesktopExpandedPosition.top,
          left: fixedDesktopExpandedPosition.left,
        });
        return undefined;
      }

      const updatePosition = () => {
        if (minimalContainerRef.current) {
          const rect = minimalContainerRef.current.getBoundingClientRect();
          
          // Expanded card dimensions (56% larger than base - 250px * 1.56 = 390px)
          const expandedCardWidth = 390; // Actual expanded card width (250 * 1.56)
          const baseCardHeight = 380; // ordersStyles.container.height
          const buttonSpace = 80; // Space for buttons below the card
          const baseTotalHeight = baseCardHeight + buttonSpace;
          
          // SCALED dimensions - the card will be rendered with transform: scale(${scale})
          // Use expanded card width for accurate clamping
          const effectiveExpandedScale = scale * EXPANDED_CARD_UNIFORM_SCALE;
          const scaledCardWidth = expandedCardWidth * effectiveExpandedScale;
          const scaledTotalHeight = baseTotalHeight * effectiveExpandedScale;
          const scaledHalfWidth = scaledCardWidth / 2;
          const scaledHalfHeight = scaledTotalHeight / 2;
          
          const padding = 10; // Margin from viewport edges
          
          // Calculate center position of the minimal card (screen coordinates)
          let top = rect.top + rect.height / 2;
          let left = rect.left + rect.width / 2;
          
          // Get viewport dimensions
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Clamp position using SCALED dimensions to keep card within viewport
          // Horizontal clamping
          if (left - scaledHalfWidth < padding) {
            left = padding + scaledHalfWidth;
          } else if (left + scaledHalfWidth > viewportWidth - padding) {
            left = viewportWidth - padding - scaledHalfWidth;
          }
          
          // Vertical clamping
          if (top - scaledHalfHeight < padding) {
            top = padding + scaledHalfHeight;
          } else if (top + scaledHalfHeight > viewportHeight - padding) {
            top = viewportHeight - padding - scaledHalfHeight;
          }
          
          setExpandedPosition({ top, left });
        }
      };
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [minimal, scale, isMobile, fixedDesktopExpandedPosition]); // Note: refs don't need to be in dependency array

  const fixedDesktopBattlefieldExpand =
    Boolean(zoneName) &&
    fixedDesktopExpandedPosition &&
    Number.isFinite(fixedDesktopExpandedPosition.top) &&
    Number.isFinite(fixedDesktopExpandedPosition.left);

  // Desktop battlefield: expanded card is portaled to body; collapse hover when the pointer
  // leaves the minimized slot and the ability/weapon icon strip (not the full expanded card).
  useEffect(() => {
    if (isMobile || !zoneName || minimal) return undefined;
    if (inspectSelectionBorder) return undefined;
    // Fixed lower-right preview is far from the slot; BattleUI clears hover from slot mouseleave/watchdog.
    if (fixedDesktopBattlefieldExpand) return undefined;
    if (typeof onBattlefieldExpandedPointerLeaveRef.current !== 'function') return undefined;

    const padPx = 22;
    const leaveDebounceMs = 95;
    let rafId = 0;
    let leaveTimerId = null;
    let hasSeenInside = false;

    const isPointerOverVisibleAbilityTooltip = (clientX, clientY) => {
      if (typeof document === 'undefined') return false;
      const nodes = document.querySelectorAll('[data-coda-ability-tooltip-root]');
      for (let i = 0; i < nodes.length; i += 1) {
        const el = nodes[i];
        const st = window.getComputedStyle(el);
        if (st.visibility === 'hidden') continue;
        const op = parseFloat(st.opacity);
        if (!Number.isFinite(op) || op < 0.05) continue;
        const r = el.getBoundingClientRect();
        if (
          clientX >= r.left &&
          clientX <= r.right &&
          clientY >= r.top &&
          clientY <= r.bottom
        ) {
          return true;
        }
      }
      return false;
    };

    const evaluate = (clientX, clientY) => {
      const minimalEl = minimalContainerRef.current;
      const portalEl = fixedDesktopBattlefieldExpand ? containerRef.current : null;
      const iconsEl = expandedAbilityIconsRowRef.current;
      const buttonsEl = expandedActionButtonsRef.current;
      const rects = [
        minimalEl ? minimalEl.getBoundingClientRect() : null,
        portalEl ? portalEl.getBoundingClientRect() : null,
        iconsEl ? iconsEl.getBoundingClientRect() : null,
        buttonsEl ? buttonsEl.getBoundingClientRect() : null,
      ];
      const inside =
        isInsideBattlefieldUnitExpandHotZoneWithUnion(clientX, clientY, rects, padPx) ||
        isPointerOverVisibleAbilityTooltip(clientX, clientY);

      if (inside) {
        hasSeenInside = true;
        if (leaveTimerId != null) {
          window.clearTimeout(leaveTimerId);
          leaveTimerId = null;
        }
        return;
      }

      if (!hasSeenInside) return;

      if (leaveTimerId == null) {
        leaveTimerId = window.setTimeout(() => {
          leaveTimerId = null;
          onBattlefieldExpandedPointerLeaveRef.current();
        }, leaveDebounceMs);
      }
    };

    const onPointerMove = (ev) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        evaluate(ev.clientX, ev.clientY);
      });
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      if (rafId) cancelAnimationFrame(rafId);
      if (leaveTimerId != null) {
        window.clearTimeout(leaveTimerId);
        leaveTimerId = null;
      }
    };
  }, [minimal, zoneName, isMobile, inspectSelectionBorder, fixedDesktopBattlefieldExpand]);

// Shared mobile expanded portal - used by ALL order types when touched on mobile
const mobileExpandedPortal = isMobile && isTouching && createPortal(
  <div
    style={{
      position: 'fixed',
      top: expandedPosition.top,
      left: expandedPosition.left,
      transform: `scale(${mobileExpandScale * EXPANDED_CARD_UNIFORM_SCALE})`,
      transformOrigin: 'top left',
      zIndex: 99999,
      margin: 0,
      width: '390px',
      height: 'auto',
      fontFamily: "'Exo 2', sans-serif",
      fontWeight: '400',
      color: 'white',
      overflow: 'visible',
      boxSizing: 'border-box',
      cursor: 'pointer',
      textAlign: 'left',
      opacity: actualIsOOR ? 0.6 : 1,
      filter: isConfused ? 'grayscale(100%)' : 'none',
    }}
  >
    <BorderImageWithTransparency
      src={getBorderImagePath(order)}
      faction={order.faction || order.faction_name}
    />
    <ProtectSelectionHighlightRing
      showTargetBorder={showTargetBorder}
      isSelected={isSelected}
      inspectSelectionBorder={inspectSelectionBorder}
      isProtectOrder={isProtectOrder}
      inset={0}
      borderRadius="12px"
    />
    <div style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '90%',
      height: '90%',
      transform: 'translate(-50%, -50%)',
      overflow: 'hidden',
      zIndex: 0,
    }}>
      {hasPremiumVideo(order) ? (
        <video
          src={getPremiumVideoPath(order)}
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
            filter: boardStatusImageFilter,
            ...(imageAdjustment.objectPosition ? { objectPosition: imageAdjustment.objectPosition } : {}),
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : (
        <img
          draggable={imageDraggable}
          src={getOrderImageSrc(order)}
          alt={order.name}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
            filter: boardStatusImageFilter,
            ...(imageAdjustment.objectPosition ? { objectPosition: imageAdjustment.objectPosition } : {}),
          }}
          onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.jpg'; }}
        />
      )}
      {actualIsOOR && (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(128, 128, 128, 0.2)', zIndex: 3, pointerEvents: 'none',
          borderRadius: '8px',
        }} />
      )}
      {/* Clip shield shine to portrait area so it does not bleed through the border frame */}
      {hasActiveShield && (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          background: 'linear-gradient(90deg, rgba(135, 206, 235, 0.3) 0%, rgba(135, 206, 235, 0.3) 40%, rgba(255, 255, 255, 0.5) 50%, rgba(135, 206, 235, 0.3) 60%, rgba(135, 206, 235, 0.3) 100%)',
          backgroundSize: '200% 100%', animation: 'shiny 1.16s linear infinite',
          zIndex: 2, pointerEvents: 'none', borderRadius: '8px',
        }} />
      )}
      {isExploreUnknownAflame ? <ExploreUnknownAflameBurnOverlay variant="card" /> : null}
    </div>
    <div style={{
      position: 'absolute', top: `${(showProtectPlating && !isConfused) ? 32 : 42 + (isProtectOrder ? 10 : 0)}px`, left: '30px', right: '30px',
      overflow: 'visible',
      pointerEvents: 'none',
      zIndex: showProtectPlating ? 6 : 4,
    }}>
      {showProtectPlating && (
        <div style={{
          position: 'absolute',
          left: '-29%', width: '158%', top: 0, height: '52px',
          backgroundImage: 'url(/images/protect_plating.png)',
          backgroundSize: '100%', backgroundPosition: 'center',
          zIndex: 0,
        }} />
      )}
      <div style={{
        position: 'relative', zIndex: 1,
        backgroundColor: showProtectPlating ? 'transparent' : 'rgba(0, 0, 0, 0.6)',
        padding: '5px 10px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        transform: showProtectPlating ? 'scale(1.2)' : undefined,
        transformOrigin: 'center center',
      }}>
        <div style={showProtectPlating ? { ...protectPlatingEngraveStyleDark, textAlign: 'center' } : { color: 'white', fontWeight: '400', textAlign: 'center' }}>{order.name}</div>
        <div style={showProtectPlating ? { ...protectPlatingEngraveStyleDark, textAlign: 'center', fontSize: '0.9em', marginTop: '5px' } : { color: 'white', fontWeight: '400', textAlign: 'center' }}>{getDisplayCost(order)}</div>
      </div>
    </div>
    {(order.attack_rating != null || order.health != null) && (
      <div style={{
        position: 'absolute', top: '50%', left: isProtectOrder ? '45px' : '30px', right: isProtectOrder ? '45px' : '30px',
        transform: 'translateY(-50%)', zIndex: 4, pointerEvents: 'none',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        {order.attack_rating != null && (
          <div style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', padding: '5px 10px', borderRadius: '4px' }}>
            <div style={{ color: 'white' }}>
              <img draggable={imageDraggable} src="/images/attack.png" alt="attack" style={{ width: '1.15em', height: '1.15em', verticalAlign: 'middle', marginRight: '2px', display: 'inline-block' }} /> {order.attack_rating}
            </div>
          </div>
        )}
        {order.health != null && (
          <OrderHealthBadgeShell
            order={order}
            imageDraggable={imageDraggable}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', padding: '5px 10px', borderRadius: '4px' }}
          />
        )}
      </div>
    )}
    <div style={{
      position: 'absolute', bottom: '80px', left: '40px', right: '40px',
      backgroundColor: 'rgba(0, 0, 0, 0.6)', padding: '10px', fontSize: '0.95em',
      color: 'white', overflow: 'visible', zIndex: 4, pointerEvents: 'none', textAlign: 'center',
    }}>
      {isConfused ? '' : highlightAbilities(order.description)}
    </div>
    <div style={{
      position: 'absolute', bottom: '50px', left: '30px', right: '30px',
      backgroundColor: 'rgba(0, 0, 0, 0.6)', textAlign: 'center', fontSize: '0.9em',
      padding: '5px 10px', color: 'white', zIndex: 4, pointerEvents: 'none',
    }}>
      {order.type_display || order.type}
    </div>
    <AbilityIndicator order={order} goldenAbilities={ascensionHighlights?.abilities} />
  </div>,
  document.body
);

if (minimal) {
  // ✅ OOR Status: Special minimal view for OOR units in flank zones (skip when mediaOnly — use plain artwork below)
  if (actualIsOOR && !mediaOnly) {
    // Calculate OOR size based on same percentage increase as minimal units
    // Original minimal: 180px × 80px, New minimal: 200px × 100px
    // Original OOR: 170px × 75px
    // Apply same percentage increase: width × (200/180) = 1.111..., height × (100/80) = 1.25
    const oorWidth = Math.round(170 * (200 / 180)); // ≈ 189px, rounding to 190px
    const oorHeight = Math.round(75 * (100 / 80)); // = 94px, rounding to 95px

    const isLethalDeathDissipating = !!lethalDissipation;
    const OorCardFaceWrapper = zoneName ? 'div' : React.Fragment;
    const oorCardFaceWrapperProps = zoneName
      ? {
          ref: lethalCardFaceRef,
          className: withLethalDeathFadeClass(
            'order-design-card-face',
            isLethalDeathFading,
            isLethalDeathDissipating,
            isRedeemMarkedForDeath,
            isRedeemDeathFinishing
          ),
          style: {
            ...OOR_DEPLOYED_CARD_FACE_SHELL,
            ...lethalDeathFadeStyle(isLethalDeathFading, lethalDeathFadeDurationMs, isLethalDeathDissipating),
            ...((showNegativeAuraCloud || showFriendlyAuraCloud) ? { overflow: 'visible' } : {}),
          },
        }
      : {};

    const oorImageOuterShellStyle = {
      ...(zoneName
        ? {
            position: 'relative',
            width: '100%',
            height: '100%',
          }
        : {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -47%)',
            width: '78%',
            height: '138%',
          }),
      borderRadius: '8px',
      pointerEvents: 'none',
      boxSizing: 'border-box',
      overflow: 'visible',
      ...getMinimalAffordableShellGlowStyle(showTargetBorder, isSelected, isPlayable),
    };
    
    return (
      <>
      <div
        className="order-design-redeem-ghost-host"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        style={{
          ...ordersStyles.container,
          backgroundColor: 'transparent', // Remove faction color background
          width: `${oorWidth}px`,        // Increased by same percentage as minimal units
          height: `${oorHeight}px`,        // Increased by same percentage as minimal units
          margin: '-1px',    
          padding: '0px',         // Remove padding for image
          fontSize: '0.9em',     // Increased font size
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '0px solid #ccc', // Remove borders from container
          borderRadius: '8px', // Match empty slot border radius
          boxSizing: 'border-box', // Match empty slot box-sizing
          position: 'relative',
          overflow: 'visible', // Allow image to extend beyond container
          // CRITICAL: Center OOR units in zone - no translateY when in zone (zoneName provided)
          transform: zoneName ? 'scale(0.9)' : 'translateY(40px) scale(0.9)', // Scale down for OOR, but center in zone
          opacity: 0.6,           // Less transparent for OOR status
          filter: isConfused ? 'grayscale(100%)' : 'none', // Gray out confused units
          visibility: isHiddenDuringAnimation ? 'hidden' : 'visible',
        }}
      >
        <OorCardFaceWrapper {...oorCardFaceWrapperProps}>
        {showFriendlyAuraCloud ? <FriendlyAuraBorderEffect /> : null}
        {showNegativeAuraCloud ? <NegativeAuraBorderEffect /> : null}
        {/* Wrapper around slab + image only (outer shell: green afford glow; inner: clip + targeting class) */}
        <div
          style={oorImageOuterShellStyle}
        >
        <div
          className={`${getTargetingBorderClassName(showTargetBorder)}${isViewedByOpponent ? ' order-viewed-by-opponent' : ''}`.trim()}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '8px',
            pointerEvents: 'none',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
        {/* 3D background slab for OOR deployed image (reduced thickness to match minimal view mahjong strip) */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          ...(!isProtectOrder ? get3DSlabStyles(stoneColor) : {}),
          borderRadius: '8px',
          pointerEvents: 'none',
        }}>
          {!isProtectOrder && <div style={minimalSlabLightFromLowerLeft} />}
        </div>
        {/* Static image/video for OOR units - smaller and more transparent */}
        {hasPremiumVideo(order) ? (
          <video
            src={getPremiumVideoPath(order)}
            autoPlay
            loop
            muted
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
              position: 'absolute',
              top: 0,
              left: 0,
              ...getMinimalAffordableMediaBorder(showTargetBorder, isSelected, inspectSelectionBorder, isProtectOrder),
              opacity: 0.7, // Make image less transparent for OOR
            }}
            onError={(e) => {
              // Fallback to image if video fails to load
              e.target.style.display = 'none';
              const fallbackImg = document.createElement('img');
              fallbackImg.src = getOrderImageSrc(order);
              fallbackImg.alt = order.name;
              fallbackImg.style.cssText = Object.entries({
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: imageAdjustment.objectPosition || 'center center',
                borderRadius: '8px',
                position: 'absolute',
                top: '0',
                left: '0',
                opacity: 0.7
              }).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v}`).join('; ');
              fallbackImg.onerror = () => {
                fallbackImg.src = '/images/default.jpg';
              };
              e.target.parentNode.appendChild(fallbackImg);
            }}
          />
        ) : lazyLoad ? (
          <LazyImage
            draggable={imageDraggable}
            src={getOrderImageSrc(order)}
            alt={order.name}
            fallbackSrc="/images/default.jpg"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: imageAdjustment.objectPosition || 'center center',
              borderRadius: '8px',
              position: 'absolute',
              top: 0,
              left: 0,
              filter: boardStatusImageFilter,
              ...getMinimalAffordableMediaBorder(showTargetBorder, isSelected, inspectSelectionBorder, isProtectOrder),
              opacity: 0.7,
            }}
            placeholder={false}
            draggable={imageDraggable}
          />
        ) : (
          <img
            draggable={imageDraggable}
            src={getOrderImageSrc(order)}
            alt={order.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: imageAdjustment.objectPosition || 'center center',
              borderRadius: '8px',
              position: 'absolute',
              top: 0,
              left: 0,
              filter: boardStatusImageFilter,
              ...getMinimalAffordableMediaBorder(showTargetBorder, isSelected, inspectSelectionBorder, isProtectOrder),
              opacity: 0.7,
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/images/default.jpg';
            }}
          />
        )}
        
        {/* Protect border in minimized OOR view (units only) */}
        {isProtectOrder && (
          <BorderImageWithTransparency
            src={getBorderImagePath(order)}
            faction={order.faction || order.faction_name}
            styleOverrides={{
              width: '100%',
              height: '100%',
              transform: 'translate(-50%, -50%)',
              zIndex: 3,
            }}
          />
        )}
        <ProtectSelectionHighlightRing
          showTargetBorder={showTargetBorder}
          isSelected={isSelected}
          inspectSelectionBorder={inspectSelectionBorder}
          isProtectOrder={isProtectOrder}
        />

        {/* 🛡️ Shield Overlay for OOR units - Transparent blue overlay with shiny animation */}
        {hasActiveShield && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, rgba(135, 206, 235, 0.3) 0%, rgba(135, 206, 235, 0.3) 40%, rgba(255, 255, 255, 0.5) 50%, rgba(135, 206, 235, 0.3) 60%, rgba(135, 206, 235, 0.3) 100%)',
              backgroundSize: '200% 100%',
              animation: 'shiny 1.16s linear infinite',
              zIndex: 2,
              pointerEvents: 'none',
              borderRadius: '8px',
            }}
          />
        )}
        {isExploreUnknownAflame ? <ExploreUnknownAflameBurnOverlay variant="card" /> : null}
        </div>
        </div>
        
        {/* Overlays positioned relative to root (slot) so they match original OOR layout */}
        {/* Overlay essential info - smaller for OOR; protect: plating image + white engrave (bar 3% longer) */}
        <div style={{
          position: 'absolute',
          bottom: zoneName ? '22px' : '3px',
          left: zoneName ? '0' : 'calc(15px + 3.5%)',
          right: zoneName ? '0' : 'calc(15px + 3.5%)',
          overflow: 'visible',
          pointerEvents: 'none',
        }}>
          {showProtectPlating && (
            <div style={{
              position: 'absolute',
              left: '-16%', width: '132%', top: '-10%', height: '120%',
            backgroundImage: 'url(/images/protect_plating.png)',
            backgroundSize: '100%', backgroundPosition: 'center',
            zIndex: 0,
            }} />
          )}
          <div style={{
            position: 'relative', zIndex: 1,
            backgroundColor: showProtectPlating ? 'transparent' : 'rgba(0, 0, 0, 0.8)',
            padding: '1px 3px',
            borderRadius: '3px',
            fontSize: '0.75em',
            color: showProtectPlating ? undefined : 'white',
            textAlign: 'center',
            ...(showProtectPlating ? protectPlatingEngraveStyleDark : {}),
          }}>
            {order.name} ({getDisplayCost(order)})
          </div>
        </div>
        
        {/* Cost indicator circles for OOR - 12 circles, filled based on cost (only show when not in a zone). Below the order name, a few pixels up so still inside the image. */}
        {!zoneName && (
          <div
            style={{
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '2px',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              padding: '3px 6px',
              borderRadius: '5px',
            }}
          >
            {Array.from({ length: 12 }, (_, index) => {
              const cost = getDisplayCost(order);
              const isFilled = index < cost;
              // Calculate if player can afford this circle
              // If player has 5 intel and order costs 6, circles 0-4 should be normal blue, 5 should be darker blue
              const canAfford = playerIntelligence !== null && index < Math.min(cost, playerIntelligence);
              // Use darker blue for filled circles that player cannot afford
              const backgroundColor = isFilled 
                ? (canAfford ? '#4A90E2' : '#2E5A8A') // Normal blue if affordable, darker blue if not
                : 'rgba(255, 255, 255, 0.3)'; // Empty circles stay the same
              return (
                <div
                  key={index}
                  style={{
                    width: '5px',
                    height: '5px',
                    borderRadius: '50%',
                    backgroundColor: backgroundColor,
                    border: isFilled ? 'none' : '1px solid rgba(255, 255, 255, 0.5)',
                  }}
                />
              );
            })}
          </div>
        )}
        
        {/* Attack rating overlay - smaller for OOR (4% inwards from edge) */}
        {order.attack_rating != null && (
          <div
            style={{
              position: 'absolute',
              top: zoneName ? '29px' : '15px',
              left: zoneName
                ? (isProtectOrder ? '14px' : '2px')
                : `calc(${isProtectOrder ? 30 : 15}px + 4%)`,
              fontSize: '0.75em', // Increased font size
              fontWeight: 'bold',
              color: order.strategemReduced ? '#ff6b6b' : 'white',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              backgroundColor: 'rgba(0, 0, 0, 0.8)', // Darker background
              padding: '1px 3px', // Smaller padding
              borderRadius: '2px',
            }}
          >
            <img draggable={imageDraggable} src="/images/attack.png" alt="attack" style={{ width: '1.15em', height: '1.15em', verticalAlign: 'middle', marginRight: '2px', display: 'inline-block' }} /> {order.attack_rating}
            {order.strategemReduced && order.strategemReductionAmount && (
              <span style={{ fontSize: '0.6em', color: '#ff4444' }}>
                {' '}(-{order.strategemReductionAmount})
              </span>
            )}
          </div>
        )}
        
        {/* Health overlay - smaller for OOR (4% inwards from edge) */}
        {order.health != null && (
          <OrderHealthBadgeShell
            order={order}
            imageDraggable={imageDraggable}
            style={{
              position: 'absolute',
              top: zoneName ? '29px' : '15px',
              right: zoneName
                ? (isProtectOrder ? '14px' : '2px')
                : `calc(${isProtectOrder ? 30 : 15}px + 4%)`,
              fontSize: '0.75em',
              fontWeight: 'bold',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: '1px 3px',
              borderRadius: '2px',
            }}
          />
        )}
        
        {/* Immobilized status indicator (minimal/OOR view) */}
        {isImmobilized && order.zone && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '128px',
              height: '128px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 5,
              animation: 'immobilizedPulse 2s ease-in-out infinite',
              pointerEvents: 'none',
            }}
            title="Immobilized"
          >
            <img
              draggable={imageDraggable}
              src="/images/immobilize.png"
              alt="Immobilized"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
        
        {/* Sleeping zZz for units that cannot attack - OOR minimal view */}
        {(minimal || keepMinimalVisibleWhenExpanded) && cannotAttack && !isImmobilized && (order.zone || zoneName) && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 6,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              fontFamily: 'Arial, sans-serif',
              fontWeight: 'bold',
            }}
          >
            <span style={{ fontSize: '1.4em', animation: 'growZ 1.5s ease-in-out infinite' }}>z</span>
            <span style={{ fontSize: '2em', animation: 'growZ 1.5s ease-in-out infinite 0.2s' }}>Z</span>
            <span style={{ fontSize: '1.4em', animation: 'growZ 1.5s ease-in-out infinite 0.4s' }}>z</span>
          </div>
        )}
        
        {/* ✅ REMOVED: Old damage animations - two-phase attack system handles attack damage animations */}
        <AbilityIndicator order={order} compact useCardFaceAnchor={!!zoneName} />
        
        {/* Ability symbol animations for minimal OOR view */}
        {animatingAbilities.map((anim) => (
          <AbilitySymbolAnimation
            key={anim.id}
            ability={anim.ability}
            delay={anim.delay}
            onComplete={() => removeAnimation(anim.id)}
          />
        ))}
        </OorCardFaceWrapper>
        <RedeemGhostMarkOverlay
          show={isRedeemMarkedForDeath || showCaropaeraRedeemGhost}
          ascend={isRedeemDeathFinishing}
          instanceId={order?.instanceId}
        />
      </div>
      {/* Shared mobile expanded portal for OOR units */}
      {mobileExpandedPortal}
      {lethalDeathParticlePortal}
      </>
    );
  }
  // Closing the if (minimal) block
}
  // If minimal is true but not OOR, fall through to render minimal view below
  // This code runs when minimal is false (expanded view) OR minimal is true but not OOR (minimal view only)
  
  // If we reach here, minimal is false (expanded view) OR minimal is true but not OOR
  // Container that maintains layout position - always minimal size
  const isRepositioning = order?._isRepositioning;
  
  // Check if this is an always-expanded order (like in mulligan, counter strike dialog, or Strategy.js) - no minimal container needed
  // Mulligan/Counter Strike orders: minimal=false, no zoneName, isMulligan/isCounterStrike=true
  // Strategy.js orders: minimal=false, no zoneName, no gameState (or gameState but not in battlefield)
  // Hand orders: minimal={!isHovered}, so they have minimal container even when hovered
  const isAlwaysExpanded = !minimal && !zoneName && (isMulligan || isCounterStrike || !gameState);
  const keepMinimalVisibleWhenExpanded = (
    !hideExpandedPortalSourceSlot &&
    !minimal &&
    !isMobile &&
    fixedDesktopExpandedPosition &&
    Number.isFinite(fixedDesktopExpandedPosition.top) &&
    Number.isFinite(fixedDesktopExpandedPosition.left)
  );
  
  // For always-expanded orders (mulligan), render directly without minimal container wrapper
  if (isAlwaysExpanded) {
    return (
      <div
        style={{
          position: 'relative',
          margin: 0, // Grid gap handles spacing
          width: '390px', // 56% increase from 250px (250 * 1.56 = 390) - 30% then another 20%
          height: 'auto', // Let border image determine height
          fontFamily: "'Exo 2', sans-serif",
          fontWeight: '400',
          color: 'white',
          overflow: 'visible', // Border must be visible
          boxSizing: 'border-box',
          cursor: 'pointer',
          textAlign: 'left',
          opacity: actualIsOOR ? 0.6 : 1,
          filter: (() => {
            const base = isConfused ? 'grayscale(100%)' : 'none';
            if (showTargetBorder || isSelected) return base;
            const drops = [];
            if (isPlayable) {
              drops.push('drop-shadow(0 0 15px rgba(0,255,0,0.9))', 'drop-shadow(0 0 30px rgba(0,255,0,0.7))', 'drop-shadow(0 0 45px rgba(0,255,0,0.5))');
            }
            return drops.length > 0 ? `${base} ${drops.join(' ')}`.trim() : base;
          })(),
          pointerEvents: expandedPortalPointerEvents,
          visibility: isHiddenDuringAnimation ? 'hidden' : 'visible',
          border: getSelectionBorderCss(showTargetBorder, isSelected, inspectSelectionBorder),
          boxShadow: (showTargetBorder || isSelected || isPlayable) ? 'none' : 'none',
        }}
        onClick={onClick}
        ref={containerRef}
        onMouseMove={(e) => {
          if (!containerRef.current) return;
          const rect = containerRef.current.getBoundingClientRect();
          const y = e.clientY - rect.top;
          const isInImageArea = y <= rect.height;
          if (isInImageArea !== imageHovered) setImageHovered(isInImageArea);
        }}
        onMouseLeave={() => {
          if (imageHovered) setImageHovered(false);
        }}
      >
        {/* Border image - determines card size, 56% larger overall (30% then another 20%), faction-specific */}
        <BorderImageWithTransparency
          src={getBorderImagePath(order)}
          faction={order.faction || order.faction_name}
          onLoad={(naturalWidth, naturalHeight) => {
            // Set container size to accommodate the 56% scaled border
            if (containerRef.current && naturalHeight && naturalWidth) {
              const aspectRatio = naturalHeight / naturalWidth;
              const baseWidth = 250; // Original width
              const scaledWidth = baseWidth * 1.56; // 56% larger (390px) - 30% then another 20%
              const scaledHeight = scaledWidth * aspectRatio; // Maintain aspect ratio
              containerRef.current.style.width = `${scaledWidth}px`;
              containerRef.current.style.height = `${scaledHeight}px`;
            }
          }}
        />
        <ProtectSelectionHighlightRing
          showTargetBorder={showTargetBorder}
          isSelected={isSelected}
          inspectSelectionBorder={inspectSelectionBorder}
          isProtectOrder={isProtectOrder}
          inset={0}
          borderRadius="12px"
        />
        
        {/* Image container - clips images to border's inner content area, 10% smaller to fit */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '90%',
          height: '90%',
          transform: 'translate(-50%, -50%)',
          overflow: 'hidden',
          borderRadius: (order?.faction || order?.faction_name) === 'The Sentients' ? '16px' : '12px',
          zIndex: 0,
        }}>
        {/* Full card image - fits inside border, clipped to border edges */}
        {hasPremiumVideo(order) ? (
          <video
            src={getPremiumVideoPath(order)}
            autoPlay
            loop
            muted
            playsInline
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              pointerEvents: 'none',
              opacity: actualIsOOR ? 0.7 : 1,
              ...(imageAdjustment.objectPosition ? { objectPosition: imageAdjustment.objectPosition } : {}),
            }}
            onError={(e) => {
              // Fallback to image if video fails to load
              e.target.style.display = 'none';
              const fallbackImg = document.createElement('img');
              fallbackImg.src = getOrderImageSrc(order);
              fallbackImg.alt = order.name;
              fallbackImg.style.cssText = Object.entries({
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none',
                opacity: actualIsOOR ? 0.7 : 1,
                ...(imageAdjustment.objectPosition ? { objectPosition: imageAdjustment.objectPosition } : {}),
              }).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v}`).join('; ');
              fallbackImg.onerror = () => {
                fallbackImg.src = '/images/default.jpg';
              };
              e.target.parentNode.appendChild(fallbackImg);
            }}
          />
        ) : lazyLoad ? (
          <LazyImage
            draggable={imageDraggable}
            src={getOrderImageSrc(order)}
            alt={order.name}
            fallbackSrc="/images/default.jpg"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              pointerEvents: 'none',
              filter: boardStatusImageFilter,
              opacity: actualIsOOR ? 0.7 : 1,
              ...(imageAdjustment.objectPosition ? { objectPosition: imageAdjustment.objectPosition } : {}),
            }}
            placeholder={true}
          />
        ) : (
          <img
            draggable={imageDraggable}
            src={getOrderImageSrc(order)}
            alt={order.name}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              pointerEvents: 'none',
              filter: boardStatusImageFilter,
              opacity: actualIsOOR ? 0.7 : 1,
              ...(imageAdjustment.objectPosition ? { objectPosition: imageAdjustment.objectPosition } : {}),
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/images/default.jpg';
            }}
          />
        )}
        <FractureMindsPoweredSkullOverlay show={fractureMindsPoweredSkull} iconSizePx={22} />
        {actualIsOOR && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(128, 128, 128, 0.2)',
              zIndex: 3,
              pointerEvents: 'none',
              borderRadius: '8px',
            }}
          />
        )}
        {/* Clip shield shine to portrait area so it does not bleed through the border frame */}
        {hasActiveShield && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, rgba(135, 206, 235, 0.3) 0%, rgba(135, 206, 235, 0.3) 40%, rgba(255, 255, 255, 0.5) 50%, rgba(135, 206, 235, 0.3) 60%, rgba(135, 206, 235, 0.3) 100%)',
              backgroundSize: '200% 100%',
              animation: 'shiny 1.16s linear infinite',
              zIndex: 2,
              pointerEvents: 'none',
              borderRadius: '8px',
            }}
          />
        )}
        {isExploreUnknownAflame ? <ExploreUnknownAflameBurnOverlay variant="card" /> : null}
        </div>
        
        {/* Title and cost at top - protect: plating image + white engrave text (20% larger); else black bar */}
        <div style={{
          position: 'absolute',
          top: `${(showProtectPlating && !isConfused) ? 32 : 42 + (isProtectOrder ? 10 : 0)}px`,
          left: '30px',
          right: '30px',
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: showProtectPlating ? 6 : 4,
        }}>
          {showProtectPlating && (
            <div style={{
              position: 'absolute',
              left: '-29%', width: '158%', top: 0, height: '52px',
              backgroundImage: 'url(/images/protect_plating.png)',
              backgroundSize: '100%', backgroundPosition: 'center',
              zIndex: 0,
            }} />
          )}
          <div style={{
            position: 'relative', zIndex: 1,
            backgroundColor: showProtectPlating ? 'transparent' : 'rgba(0, 0, 0, 0.6)',
            padding: '5px 10px',
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
            transform: showProtectPlating ? 'scale(1.2)' : undefined,
            transformOrigin: 'center center',
          }}>
            <div style={showProtectPlating ? { ...protectPlatingEngraveStyleDark, textAlign: 'center' } : { color: 'white', fontWeight: '400', textAlign: 'center' }}>{order.name}</div>
            <div style={showProtectPlating ? { ...protectPlatingEngraveStyleDark, textAlign: 'center', fontSize: '0.9em', marginTop: '5px' } : { color: 'white', fontWeight: '400', textAlign: 'center' }}>{getDisplayCost(order)}</div>
          </div>
        </div>
        
        {/* Black transparent background for attack and health stats - centered, separate bars */}
        {(order.attack_rating != null || order.health != null) && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: isProtectOrder ? '45px' : '30px',
            right: isProtectOrder ? '45px' : '30px',
            transform: 'translateY(-50%)',
            zIndex: 4,
            pointerEvents: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            {order.attack_rating != null && (
              <div style={{
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                padding: '5px 10px',
                borderRadius: '4px',
              }}>
                <div style={{
                  color: ascensionHighlights?.attack ? '#e8c547' : (order.strategemReduced ? '#ff6b6b' : 'white'),
                  backgroundColor: order.strategemReduced ? 'rgba(255, 107, 107, 0.3)' : 'transparent',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  border: order.strategemReduced ? '1px solid #ff6b6b' : 'none',
                }}>
                  <img draggable={imageDraggable} src="/images/attack.png" alt="attack" style={{ width: '1.15em', height: '1.15em', verticalAlign: 'middle', marginRight: '2px', display: 'inline-block' }} /> {order.attack_rating}
                  {order.strategemReduced && order.strategemReductionAmount && (
                    <span style={{ fontSize: '0.8em', color: '#ff4444' }}>
                      {' '}(-{order.strategemReductionAmount})
                    </span>
                  )}
                </div>
              </div>
            )}
            {order.health != null && (
              <OrderHealthBadgeShell
                order={order}
                imageDraggable={imageDraggable}
                textColor={ascensionHighlights?.health ? '#e8c547' : undefined}
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  marginRight: (factionForColor === 'The Sentients' || factionForColor === 'Asrah') ? '13px' : '0',
                }}
              />
            )}
          </div>
        )}
        
        {/* Sleeping zZz for units that cannot attack - expanded view */}
        {cannotAttack && !isImmobilized && (order.zone || zoneName) && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 6,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              fontFamily: 'Arial, sans-serif',
              fontWeight: 'bold',
            }}
          >
            <span style={{ fontSize: '2em', animation: 'growZ 1.5s ease-in-out infinite' }}>z</span>
            <span style={{ fontSize: '2.9em', animation: 'growZ 1.5s ease-in-out infinite 0.2s' }}>Z</span>
            <span style={{ fontSize: '2em', animation: 'growZ 1.5s ease-in-out infinite 0.4s' }}>z</span>
          </div>
        )}

        {/* Black transparent background for description text */}
        <div style={{
          position: 'absolute',
          bottom: '80px', // Moved up a lot
          left: '40px', // Increased margin to make bar smaller horizontally
          right: '40px', // Increased margin to make bar smaller horizontally
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          padding: '10px',
          paddingLeft: '10px', // Equal padding on both sides for true centering
          paddingRight: '10px', // Equal padding on both sides for true centering
          fontSize: '0.95em',
          color: 'white', // All units use white text
          overflow: 'visible',
          zIndex: 4,
          pointerEvents: 'none',
          textAlign: 'center',
        }}>
          {isConfused ? '' : highlightAbilities(order.description)}
        </div>
        
        {/* Black transparent background for type at bottom */}
        <div style={{
          position: 'absolute',
          bottom: '50px', // Moved up a lot
          left: '30px', // Match title bar margins
          right: '30px', // Match title bar margins
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          textAlign: 'center',
          fontSize: '0.9em',
          padding: '5px 10px',
          color: 'white', // All units use white text
          zIndex: 4,
          pointerEvents: 'none',
        }}>
          {order.type_display || order.type}
        </div>
        
        <AbilityIndicator order={order} goldenAbilities={ascensionHighlights?.abilities} />
      </div>
    );
  }
  
  const minimalImageOuterShellStyle = {
    ...(zoneName
      ? {
          position: 'relative',
          width: '100%',
          height: '100%',
        }
      : {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          height: '140%',
        }),
    borderRadius: '8px',
    pointerEvents: 'none',
    zIndex: 0,
    overflow: 'visible',
    ...getMinimalAffordableShellGlowStyle(showTargetBorder, isSelected, isPlayable),
  };

  const minimalPortraitOverlayPosition = zoneName
    ? { position: 'absolute', inset: 0, width: '100%', height: '100%' }
    : {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        height: '140%',
      };

  const isLethalDeathDissipating = !!lethalDissipation;
  const MinimalCardFaceWrapper = zoneName ? 'div' : React.Fragment;
  const minimalCardFaceWrapperProps = zoneName
    ? {
        ref: lethalCardFaceRef,
        className: withLethalDeathFadeClass(
          'order-design-card-face',
          isLethalDeathFading,
          isLethalDeathDissipating,
          isRedeemMarkedForDeath,
          isRedeemDeathFinishing
        ),
        style: {
          ...MINIMAL_DEPLOYED_CARD_FACE_SHELL,
          ...lethalDeathFadeStyle(isLethalDeathFading, lethalDeathFadeDurationMs, isLethalDeathDissipating),
          ...((showNegativeAuraCloud || showFriendlyAuraCloud) ? { overflow: 'visible' } : {}),
        },
      }
    : {};

  return (
    <div
      ref={minimalContainerRef}
      className="order-design-card-bounds order-design-redeem-ghost-host"
      style={{
        position: 'relative',
        width: '200px',        
        height: '100px', // Match empty slot size exactly
        margin: '-1px',    // Match empty slot margin exactly
        padding: '0px',
        // CRITICAL: Match empty slot transform exactly - no transform when in zone
        transform: isRepositioning ? undefined : (zoneName ? 'none' : 'translateY(40px)'),
        filter: mediaOnly ? 'none' : (isConfused ? 'grayscale(100%)' : 'none'), // Gray out confused units
        visibility: isHiddenDuringAnimation ? 'hidden' : 'visible',
      }}
    >
      {/* Minimal view - always rendered to maintain layout space */}
      <div
        className={minimal ? 'order-design-minimal' : ''}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        style={{
          ...ordersStyles.container,
          backgroundColor: 'transparent', // Remove faction color background
          width: '200px',        
          height: '100px', // Match empty slot size exactly
          margin: '0',
          padding: '0px', // Match empty slot padding exactly
          fontSize: '0.9em',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '0px solid #ccc', // Match empty slot border exactly
          borderRadius: '8px', // Match empty slot border radius
          boxSizing: 'border-box', // Match empty slot box-sizing exactly
          position: 'relative',
          zIndex: 1,
          overflow: 'visible', // Match empty slot overflow
          // If expanded portal is pinned elsewhere, keep the source card visible in its slot.
          opacity: (minimal || isMobile || keepMinimalVisibleWhenExpanded) ? 1 : 0,
          pointerEvents: (minimal || isMobile || keepMinimalVisibleWhenExpanded) ? 'auto' : 'none',
        }}
      >
      {minimal && mediaOnly ? (
        <div
          className={[
            isRedeemMarkedForDeath && !isRedeemDeathFinishing ? 'order-design-redeem-marked-for-death' : '',
            isRedeemDeathFinishing ? 'order-design-redeem-death-finishing' : '',
          ].filter(Boolean).join(' ') || undefined}
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '8px',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          {hasPremiumVideo(order) ? (
            <video
              src={getPremiumVideoPath(order)}
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: imageAdjustment.objectPosition || 'center center',
                display: 'block',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                const fallbackImg = document.createElement('img');
                fallbackImg.src = getOrderImageSrc(order);
                fallbackImg.alt = '';
                fallbackImg.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
                fallbackImg.onerror = () => {
                  fallbackImg.src = '/images/default.jpg';
                };
                e.target.parentNode.appendChild(fallbackImg);
              }}
            />
          ) : lazyLoad ? (
            <LazyImage
              draggable={imageDraggable}
              src={getOrderImageSrc(order)}
              alt=""
              fallbackSrc="/images/default.jpg"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: imageAdjustment.objectPosition || 'center center',
                display: 'block',
              }}
              placeholder={false}
            />
          ) : (
            <img
              draggable={imageDraggable}
              src={getOrderImageSrc(order)}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: imageAdjustment.objectPosition || 'center center',
                display: 'block',
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/images/default.jpg';
              }}
            />
          )}
        </div>
      ) : (
      <>
      <MinimalCardFaceWrapper {...minimalCardFaceWrapperProps}>
      {showFriendlyAuraCloud ? <FriendlyAuraBorderEffect /> : null}
      {showNegativeAuraCloud ? <NegativeAuraBorderEffect /> : null}
      {/* Static image replaces minimal display - smaller (same element that gets yellow strategem target border) */}
      {lazyLoad ? (
        <div
          style={minimalImageOuterShellStyle}
        >
        <div
          className={`${getTargetingBorderClassName(showTargetBorder)}${isViewedByOpponent ? ' order-viewed-by-opponent' : ''}`.trim()}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '8px',
            pointerEvents: 'none',
            overflow: 'hidden',
            position: 'relative',
            ...(!isProtectOrder ? get3DSlabStyles(stoneColor) : {}),
            paddingBottom: isProtectOrder ? '0px' : '4px',
          }}
        >
          {!isProtectOrder && <div style={minimalSlabLightFromLowerLeft} />}
          {hasPremiumVideo(order) ? (
            <video
              src={getPremiumVideoPath(order)}
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
                filter: boardStatusImageFilter,
                ...getMinimalAffordableMediaBorder(showTargetBorder, isSelected, inspectSelectionBorder, isProtectOrder),
              }}
              onError={(e) => {
                // Fallback to image if video fails to load
                e.target.style.display = 'none';
                const fallbackImg = document.createElement('img');
                fallbackImg.src = getOrderImageSrc(order);
                fallbackImg.alt = order.name;
                fallbackImg.style.cssText = Object.entries({
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: imageAdjustment.objectPosition || 'center center',
                  borderRadius: '8px',
                  filter: boardStatusImageFilter,
                }).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v}`).join('; ');
                fallbackImg.onerror = () => {
                  fallbackImg.src = '/images/default.jpg';
                };
                e.target.parentNode.appendChild(fallbackImg);
              }}
            />
          ) : (
            <LazyImage
              draggable={imageDraggable}
              src={getOrderImageSrc(order)}
              alt={order.name}
              fallbackSrc="/images/default.jpg"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: imageAdjustment.objectPosition || 'center center',
                borderRadius: '8px',
                ...getMinimalAffordableMediaBorder(showTargetBorder, isSelected, inspectSelectionBorder, isProtectOrder),
            }}
            placeholder={false}
          />
          )}
          {isProtectOrder && (
            <BorderImageWithTransparency
              src={getBorderImagePath(order)}
              faction={order.faction || order.faction_name}
              styleOverrides={{
                width: '100%',
                height: '100%',
                transform: 'translate(-50%, -50%)',
                zIndex: 3,
              }}
            />
          )}
          <ProtectSelectionHighlightRing
            showTargetBorder={showTargetBorder}
            isSelected={isSelected}
            inspectSelectionBorder={inspectSelectionBorder}
            isProtectOrder={isProtectOrder}
          />
          <FractureMindsPoweredSkullOverlay show={fractureMindsPoweredSkull} iconSizePx={14} />
        </div>
        </div>
      ) : (
        <div
          style={minimalImageOuterShellStyle}
        >
        <div
          className={`${getTargetingBorderClassName(showTargetBorder)}${isViewedByOpponent ? ' order-viewed-by-opponent' : ''}`.trim()}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '8px',
            pointerEvents: 'none',
            overflow: 'hidden',
            position: 'relative',
            ...(!isProtectOrder ? get3DSlabStyles(stoneColor) : {}),
            paddingBottom: isProtectOrder ? '0px' : '4px',
          }}
        >
          {!isProtectOrder && <div style={minimalSlabLightFromLowerLeft} />}
          {hasPremiumVideo(order) ? (
            <video
              src={getPremiumVideoPath(order)}
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
                filter: boardStatusImageFilter,
                ...getMinimalAffordableMediaBorder(showTargetBorder, isSelected, inspectSelectionBorder, isProtectOrder),
              }}
              onError={(e) => {
                // Fallback to image if video fails to load
                e.target.style.display = 'none';
                const fallbackImg = document.createElement('img');
                fallbackImg.src = getOrderImageSrc(order);
                fallbackImg.alt = order.name;
                fallbackImg.style.cssText = Object.entries({
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: imageAdjustment.objectPosition || 'center center',
                  borderRadius: '8px',
                }).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v}`).join('; ');
                fallbackImg.onerror = () => {
                  fallbackImg.src = '/images/default.jpg';
                };
                e.target.parentNode.appendChild(fallbackImg);
              }}
            />
          ) : (
            <img
              draggable={imageDraggable}
              src={getOrderImageSrc(order)}
              alt={order.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: imageAdjustment.objectPosition || 'center center',
                borderRadius: '8px',
                filter: boardStatusImageFilter,
                ...getMinimalAffordableMediaBorder(showTargetBorder, isSelected, inspectSelectionBorder, isProtectOrder),
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/images/default.jpg';
              }}
            />
          )}
          {isProtectOrder && (
            <BorderImageWithTransparency
              src={getBorderImagePath(order)}
              faction={order.faction || order.faction_name}
              styleOverrides={{
                width: '100%',
                height: '100%',
                transform: 'translate(-50%, -50%)',
                zIndex: 3,
              }}
            />
          )}
          <ProtectSelectionHighlightRing
            showTargetBorder={showTargetBorder}
            isSelected={isSelected}
            inspectSelectionBorder={inspectSelectionBorder}
            isProtectOrder={isProtectOrder}
          />
          <FractureMindsPoweredSkullOverlay show={fractureMindsPoweredSkull} iconSizePx={14} />
        </div>
        </div>
      )}
      
      {/* 🛡️ Shield Overlay for minimal view - Transparent blue overlay with shiny animation */}
      {hasActiveShield && (
        <div
          style={{
            ...minimalPortraitOverlayPosition,
            background: 'linear-gradient(90deg, rgba(135, 206, 235, 0.3) 0%, rgba(135, 206, 235, 0.3) 40%, rgba(255, 255, 255, 0.5) 50%, rgba(135, 206, 235, 0.3) 60%, rgba(135, 206, 235, 0.3) 100%)',
            backgroundSize: '200% 100%',
            animation: 'shiny 1.16s linear infinite',
            zIndex: 2,
            pointerEvents: 'none',
            borderRadius: '8px',
          }}
        />
      )}
      {isExploreUnknownAflame ? (
        <ExploreUnknownAflameBurnOverlay
          variant="minimal"
          style={{
            ...minimalPortraitOverlayPosition,
            zIndex: 5,
          }}
        />
      ) : null}
      
      {/* Overlay essential info - protect: plating image + white engrave text; else black bar */}
      <div
        className="order-name-display"
        style={{
          position: 'absolute',
          bottom: zoneName ? '25px' : '5px',
          left: zoneName ? '0' : '20px',
          right: zoneName ? '0' : '20px',
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: 10,
        }}>
        {showProtectPlating && (
          <div style={{
            position: 'absolute',
            left: '-16%', width: '132%', top: '-10%', height: '120%',
            backgroundImage: 'url(/images/protect_plating.png)',
            backgroundSize: '100%', backgroundPosition: 'center',
            zIndex: 0,
          }} />
        )}
        <div style={{
          position: 'relative', zIndex: 1,
          backgroundColor: showProtectPlating ? 'transparent' : 'rgba(0, 0, 0, 0.7)',
          padding: '2px 4px',
          borderRadius: '4px',
          fontSize: '0.8em',
          color: showProtectPlating ? undefined : 'white',
          textAlign: 'center',
          ...(showProtectPlating ? protectPlatingEngraveStyleDark : {}),
        }}>
          {order.name} ({getDisplayCost(order)})
        </div>
      </div>
      
      {/* Cost indicator circles - 12 circles, filled based on cost (only show when not in a zone). Below the order name, a few pixels up from the slab so still inside the image. */}
      {!zoneName && (
        <div
          style={{
            position: 'absolute',
            bottom: '-10px', // Below the order name (name at 5px); a few pixels up from -18 so inside/barely in image
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '3px',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            padding: '4px 8px',
            borderRadius: '6px',
            zIndex: 10
          }}
        >
          {Array.from({ length: 12 }, (_, index) => {
            const cost = getDisplayCost(order);
            const isFilled = index < cost;
            // Calculate if player can afford this circle
            // If player has 5 intel and order costs 6, circles 0-4 should be normal blue, 5 should be darker blue
            // Example: playerIntelligence=5, cost=6
            //   index 0-4: index < min(6,5)=5 → true (normal blue)
            //   index 5: 5 < min(6,5)=5 → false (darker blue)
            const canAfford = playerIntelligence !== null && index < Math.min(cost, playerIntelligence);
            // Use darker blue for filled circles that player cannot afford
            const backgroundColor = isFilled 
              ? (canAfford ? '#4A90E2' : '#2E5A8A') // Normal blue if affordable, darker blue if not
              : 'rgba(255, 255, 255, 0.3)'; // Empty circles stay the same
            return (
              <div
                key={index}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: backgroundColor,
                  border: isFilled ? 'none' : '1px solid rgba(255, 255, 255, 0.5)',
                }}
              />
            );
          })}
        </div>
      )}
      
      {/* Cost display for mobile - separate element for mobile styling */}
      <div 
        className="mobile-cost-display"
        style={{ 
          position: 'absolute', 
          bottom: '5px', 
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          padding: '2px 4px',
          borderRadius: '4px',
          fontSize: '0.8em',
          color: 'white',
          textAlign: 'center',
          display: 'none' // Hidden by default, shown in mobile via CSS
        }}>
          {getDisplayCost(order)}
      </div>
      
        {/* Attack rating overlay - moved 1 cell in + 10px */}
        {order.attack_rating != null && (
          <div
            className="order-attack-rating"
            style={{
              position: 'absolute',
              top: zoneName ? '40px' : '20px',
              left: zoneName ? (isProtectOrder ? '15px' : '0') : (isProtectOrder ? '35px' : '20px'),
            fontSize: '0.9em', // Increased font size
            fontWeight: 'bold',
            color: order.strategemReduced ? '#ff6b6b' : 'white',
            textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            padding: '2px 4px',
            borderRadius: '3px',
            zIndex: 10
          }}
        >
          <img draggable={imageDraggable} src="/images/attack.png" alt="attack" className="attack-icon" style={{ width: '1.15em', height: '1.15em', verticalAlign: 'middle', marginRight: '2px', display: 'inline-block' }} /> {order.attack_rating}
          {order.strategemReduced && order.strategemReductionAmount && (
            <span style={{ fontSize: '0.8em', color: '#ff4444' }}>
              {' '}(-{order.strategemReductionAmount})
            </span>
          )}
        </div>
      )}
      
        {/* Health overlay - moved 1 cell in + 10px */}
        {order.health != null && (
          <OrderHealthBadgeShell
            order={order}
            imageDraggable={imageDraggable}
            useHealthRatingShell
            healthIconClassName="health-icon"
            style={{
              position: 'absolute',
              top: zoneName ? '40px' : '20px',
              right: zoneName ? (isProtectOrder ? '15px' : '0') : (isProtectOrder ? '35px' : '20px'),
              fontSize: '0.9em',
              fontWeight: 'bold',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              padding: '2px 4px',
              borderRadius: '3px',
              zIndex: order.healthCountdownPulse ? 100 : 10,
            }}
          />
        )}
      
      {/* Immobilized status indicator */}
      {isImmobilized && order.zone && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '168px',
            height: '168px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
            animation: 'immobilizedPulse 2s ease-in-out infinite',
            pointerEvents: 'none',
          }}
          title="Immobilized"
        >
          <img
            draggable={imageDraggable}
            src="/images/immobilize.png"
            alt="Immobilized"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
      )}
        
        {/* Sleeping zZz for units that cannot attack - normal minimal view */}
        {(minimal || keepMinimalVisibleWhenExpanded) && cannotAttack && !isImmobilized && (order.zone || zoneName) && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 6,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              fontFamily: 'Arial, sans-serif',
              fontWeight: 'bold',
            }}
          >
            <span style={{ fontSize: '1.8em', animation: 'growZ 1.5s ease-in-out infinite' }}>z</span>
            <span style={{ fontSize: '2.6em', animation: 'growZ 1.5s ease-in-out infinite 0.2s' }}>Z</span>
            <span style={{ fontSize: '1.8em', animation: 'growZ 1.5s ease-in-out infinite 0.4s' }}>z</span>
        </div>
      )}
      
      {/* ✅ REMOVED: Old damage animations - two-phase attack system handles attack damage animations */}
      <AbilityIndicator order={order} compact useCardFaceAnchor={!!zoneName} />
      
      {/* Ability symbol animations for normal minimal view */}
      {animatingAbilities.map((anim) => (
        <AbilitySymbolAnimation
          key={anim.id}
          ability={anim.ability}
          delay={anim.delay}
          onComplete={() => removeAnimation(anim.id)}
        />
      ))}
      </MinimalCardFaceWrapper>
      </>
      )}
      
      </div>
      <RedeemGhostMarkOverlay
        show={isRedeemMarkedForDeath || showCaropaeraRedeemGhost}
        ascend={isRedeemDeathFinishing}
        instanceId={order?.instanceId}
      />
      
      {/* Expanded view portal - shows expanded card at fixed position */}
      {/* Desktop: shows when minimal=false (hovered hand cards) - NOT on mobile */}
      {/* Mobile: ONLY shows when touching (tap/hold to expand) */}
      {!mediaOnly && ((!minimal && !isMobile) || (isMobile && isTouching)) && createPortal(
        <div
          data-coda-fixed-order-preview={
            fixedDesktopExpandedPosition &&
            Number.isFinite(fixedDesktopExpandedPosition.top) &&
            Number.isFinite(fixedDesktopExpandedPosition.left)
              ? ''
              : undefined
          }
          style={{
            position: 'relative',
            margin: 0,
            width: '390px', // 56% increase from 250px (250 * 1.56 = 390) - 30% then another 20%
            height: 'auto', // Let border image determine height
            fontFamily: "'Exo 2', sans-serif",
            fontWeight: '400',
            color: 'white',
            overflow: 'visible', // Border must be visible
            boxSizing: 'border-box',
            cursor: 'pointer',
            textAlign: 'left',
            opacity: actualIsOOR ? 0.6 : 1,
            filter: (() => {
              const base = isConfused ? 'grayscale(100%)' : 'none';
              if (showTargetBorder || isSelected) return base;
              const drops = [];
              if (isPlayable) {
                drops.push('drop-shadow(0 0 15px rgba(0,255,0,0.9))', 'drop-shadow(0 0 30px rgba(0,255,0,0.7))', 'drop-shadow(0 0 45px rgba(0,255,0,0.5))');
              }
              return drops.length > 0 ? `${base} ${drops.join(' ')}`.trim() : base;
            })(),
            pointerEvents: expandedPortalPointerEvents,
            visibility: isHiddenDuringAnimation ? 'hidden' : 'visible',
            border: getSelectionBorderCss(showTargetBorder, isSelected, inspectSelectionBorder),
            boxShadow: 'none',
            // Portal-specific positioning
            position: 'fixed',
            top: expandedPosition.top,
            left: expandedPosition.left,
            // Viewport scaling: Mobile uses smaller scale and positions from top-left
            // Desktop uses center positioning.
            // The trailing rotate(0.001deg) is intentional and ~invisible (~0.007px): Chromium only
            // promotes a layer to a Windows DirectComposition hardware overlay when its transform is
            // a pure scale-or-translation (see swap_chain_presenter.cc -> transform.IsScaleOrTranslation()).
            // In fullscreen that overlay plane drops/reflashes during heavy board repaints, which is
            // the lower-right preview flicker. A tiny rotation makes the transform non-scale/translation
            // so the element stays on the normal composited path (no overlay = no flicker), while
            // z-index, hover, position, scale and tooltip access are all unchanged.
            transform: isMobile && isTouching
              ? `scale(${mobileExpandScale * EXPANDED_CARD_UNIFORM_SCALE}) rotate(0.001deg)`
              : `translate(-50%, -50%) scale(${scale * EXPANDED_CARD_UNIFORM_SCALE}) rotate(0.001deg)`,
            transformOrigin: isMobile && isTouching ? 'top left' : 'center',
            // Cache this element (incl. its drop-shadow glow filter) as a stable composited layer so
            // the heavy board repaints during a drag don't re-rasterize the expensive filter and make
            // it flicker. Paired with the rotate(0.001deg) above (which keeps it off a hardware overlay
            // plane), this leaves it a normal cached layer: no overlay flicker and no re-raster flicker.
            willChange: 'transform',
            zIndex: fixedDesktopBattlefieldExpand ? CURSOR_Z_INDEX.fixedOrderPreview : 99999,
          }}
          onClick={onClick}
          ref={containerRef}
          onMouseMove={(e) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const isInImageArea = y <= rect.height;
            if (isInImageArea !== imageHovered) setImageHovered(isInImageArea);
          }}
          onMouseLeave={() => {
            if (imageHovered) setImageHovered(false);
          }}
        >
          {/* Border image - determines card size, 56% larger overall (30% then another 20%), faction-specific */}
          <BorderImageWithTransparency
            src={getBorderImagePath(order)}
            faction={order.faction || order.faction_name}
            onLoad={(naturalWidth, naturalHeight) => {
              // Set container size to accommodate the 56% scaled border
              if (containerRef.current && naturalHeight && naturalWidth) {
                const aspectRatio = naturalHeight / naturalWidth;
                const baseWidth = 250; // Original width
                const scaledWidth = baseWidth * 1.56; // 56% larger (390px) - 30% then another 20%
                const scaledHeight = scaledWidth * aspectRatio; // Maintain aspect ratio
                containerRef.current.style.width = `${scaledWidth}px`;
                containerRef.current.style.height = `${scaledHeight}px`;
              }
            }}
          />
          <ProtectSelectionHighlightRing
            showTargetBorder={showTargetBorder}
            isSelected={isSelected}
            inspectSelectionBorder={inspectSelectionBorder}
            isProtectOrder={isProtectOrder}
            inset={0}
            borderRadius="12px"
          />
          
          {/* Image container - clips images to border's inner content area, 10% smaller to fit */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '90%',
            height: '90%',
            transform: 'translate(-50%, -50%)',
            overflow: 'hidden',
            borderRadius: (order?.faction || order?.faction_name) === 'The Sentients' ? '16px' : '12px',
            zIndex: 0,
          }}>
          {/* Full card image - fits inside border, clipped to border edges */}
          {hasPremiumVideo(order) ? (
            <video
              src={getPremiumVideoPath(order)}
              autoPlay
              loop
              muted
              playsInline
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none',
                opacity: actualIsOOR ? 0.7 : 1,
                ...(imageAdjustment.objectPosition ? { objectPosition: imageAdjustment.objectPosition } : {}),
              }}
              onError={(e) => {
                // Fallback to image if video fails to load
                e.target.style.display = 'none';
                const fallbackImg = document.createElement('img');
                fallbackImg.src = getOrderImageSrc(order);
                fallbackImg.alt = order.name;
                fallbackImg.style.cssText = Object.entries({
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  pointerEvents: 'none',
                  opacity: actualIsOOR ? 0.7 : 1,
                  ...(imageAdjustment.objectPosition ? { objectPosition: imageAdjustment.objectPosition } : {}),
                }).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v}`).join('; ');
                fallbackImg.onerror = () => {
                  fallbackImg.src = '/images/default.jpg';
                };
                e.target.parentNode.appendChild(fallbackImg);
              }}
            />
          ) : lazyLoad ? (
            <LazyImage
              draggable={imageDraggable}
              src={getOrderImageSrc(order)}
              alt={order.name}
              fallbackSrc="/images/default.jpg"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none',
                filter: boardStatusImageFilter,
                opacity: actualIsOOR ? 0.7 : 1,
                ...(imageAdjustment.objectPosition ? { objectPosition: imageAdjustment.objectPosition } : {}),
              }}
              placeholder={true}
            />
          ) : (
            <img
              draggable={imageDraggable}
              src={getOrderImageSrc(order)}
              alt={order.name}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none',
                filter: boardStatusImageFilter,
                opacity: actualIsOOR ? 0.7 : 1,
                ...(imageAdjustment.objectPosition ? { objectPosition: imageAdjustment.objectPosition } : {}),
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/images/default.jpg';
            }}
          />
        )}
        <FractureMindsPoweredSkullOverlay show={fractureMindsPoweredSkull} iconSizePx={22} />
        {actualIsOOR && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(128, 128, 128, 0.2)',
              zIndex: 3,
              pointerEvents: 'none',
              borderRadius: '8px',
            }}
          />
        )}
        {/* Clip shield shine to portrait area so it does not bleed through the border frame */}
        {hasActiveShield && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, rgba(135, 206, 235, 0.3) 0%, rgba(135, 206, 235, 0.3) 40%, rgba(255, 255, 255, 0.5) 50%, rgba(135, 206, 235, 0.3) 60%, rgba(135, 206, 235, 0.3) 100%)',
              backgroundSize: '200% 100%',
              animation: 'shiny 1.16s linear infinite',
              zIndex: 2,
              pointerEvents: 'none',
              borderRadius: '8px',
            }}
          />
        )}
        {isExploreUnknownAflame ? <ExploreUnknownAflameBurnOverlay variant="card" /> : null}
        </div>
        
        {/* Title and cost at top - protect: plating image + white engrave text (20% larger); else black bar */}
        <div style={{
          position: 'absolute',
          top: `${(showProtectPlating && !isConfused) ? 32 : 42 + (isProtectOrder ? 10 : 0)}px`,
          left: '30px',
          right: '30px',
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: showProtectPlating ? 6 : 4,
        }}>
          {showProtectPlating && (
            <div style={{
              position: 'absolute',
              left: '-29%', width: '158%', top: 0, height: '52px',
              backgroundImage: 'url(/images/protect_plating.png)',
              backgroundSize: '100%', backgroundPosition: 'center',
              zIndex: 0,
            }} />
          )}
          <div style={{
            position: 'relative', zIndex: 1,
            backgroundColor: showProtectPlating ? 'transparent' : 'rgba(0, 0, 0, 0.6)',
            padding: '5px 10px',
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
              transform: showProtectPlating ? 'scale(1.2)' : undefined,
              transformOrigin: 'center center',
            }}>
              <div style={showProtectPlating ? { ...protectPlatingEngraveStyle, color: '#2a2a2a', textAlign: 'center' } : { color: 'white', fontWeight: '400', textAlign: 'center' }}>{order.name}</div>
              <div style={showProtectPlating ? { ...protectPlatingEngraveStyle, color: '#2a2a2a', textAlign: 'center', fontSize: '0.9em', marginTop: '5px' } : { color: 'white', fontWeight: '400', textAlign: 'center' }}>{getDisplayCost(order)}</div>
            </div>
          </div>
          
          {/* Black transparent background for attack and health stats - centered, separate bars */}
          {(order.attack_rating != null || order.health != null) && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: isProtectOrder ? '45px' : '30px',
              right: isProtectOrder ? '45px' : '30px',
              transform: 'translateY(-50%)',
              zIndex: 4,
              pointerEvents: 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              {order.attack_rating != null && (
                <div style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  padding: '5px 10px',
                  borderRadius: '4px',
                }}>
                  <div style={{
                    color: order.strategemReduced ? '#ff6b6b' : 'white',
                    backgroundColor: order.strategemReduced ? 'rgba(255, 107, 107, 0.3)' : 'transparent',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    border: order.strategemReduced ? '1px solid #ff6b6b' : 'none',
                  }}>
                    <img draggable={imageDraggable} src="/images/attack.png" alt="attack" style={{ width: '1.15em', height: '1.15em', verticalAlign: 'middle', marginRight: '2px', display: 'inline-block' }} /> {order.attack_rating}
                    {order.strategemReduced && order.strategemReductionAmount && (
                      <span style={{ fontSize: '0.8em', color: '#ff4444' }}>
                        {' '}(-{order.strategemReductionAmount})
                      </span>
                    )}
                  </div>
                </div>
              )}
              {order.health != null && (
                <OrderHealthBadgeShell
                  order={order}
                  imageDraggable={imageDraggable}
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    marginRight: (factionForColor === 'The Sentients' || factionForColor === 'Asrah') ? '13px' : '0',
                  }}
                />
              )}
            </div>
          )}
          
          {/* Sleeping zZz for units that cannot attack - expanded view */}
          {cannotAttack && !isImmobilized && (order.zone || zoneName) && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 6,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                fontFamily: 'Arial, sans-serif',
                fontWeight: 'bold',
              }}
            >
              <span style={{ fontSize: '2em', animation: 'growZ 1.5s ease-in-out infinite' }}>z</span>
              <span style={{ fontSize: '2.9em', animation: 'growZ 1.5s ease-in-out infinite 0.2s' }}>Z</span>
              <span style={{ fontSize: '2em', animation: 'growZ 1.5s ease-in-out infinite 0.4s' }}>z</span>
            </div>
          )}

          {/* Black transparent background for description text */}
          <div style={{
            position: 'absolute',
            bottom: '80px', // Moved up a lot
            left: '40px', // Increased margin to make bar smaller horizontally
            right: '40px', // Increased margin to make bar smaller horizontally
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            padding: '10px',
            paddingLeft: '10px', // Equal padding on both sides for true centering
            paddingRight: '10px', // Equal padding on both sides for true centering
            fontSize: '0.95em',
            color: 'white', // All units use white text
            overflow: 'visible',
            zIndex: 4,
            pointerEvents: 'none',
            textAlign: 'center',
          }}>
            {isConfused ? '' : highlightAbilities(order.description)}
          </div>
          
          {/* Black transparent background for type at bottom */}
          <div style={{
            position: 'absolute',
            bottom: '50px', // Moved up a lot
            left: '30px', // Match title bar margins
            right: '30px', // Match title bar margins
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            textAlign: 'center',
            fontSize: '0.9em',
            padding: '5px 10px',
            color: 'white', // All units use white text
            zIndex: 4,
            pointerEvents: 'none',
          }}>
            {order.type_display || order.type}
          </div>
          
          <AbilityIndicator
            order={order}
            goldenAbilities={ascensionHighlights?.abilities}
            iconsRowRef={typeof onBattlefieldExpandedPointerLeave === 'function' ? expandedAbilityIconsRowRef : undefined}
          />
          
          {/* Render action buttons for expanded card view */}
          {!minimal && renderButtons && (
            <div
              ref={typeof onBattlefieldExpandedPointerLeave === 'function' ? expandedActionButtonsRef : undefined}
              data-order-buttons-id={order?.id}
              style={{
                position: 'absolute',
                top: order?.isMission5Corn === true || order?.isExploreUnknownMemory === true ? '64%' : '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginTop: order?.isMission5Corn === true || order?.isExploreUnknownMemory === true ? '0px' : '8px',
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                alignItems: 'center',
                zIndex: 100000,
                pointerEvents: 'auto'
              }}
            >
              {renderButtons()}
            </div>
          )}
        </div>,
        document.body
      )}
      {lethalDeathParticlePortal}
    </div>
  );
}

export default OrderDesign;
